import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { format, parse } from 'date-fns';
import { 
  Upload, FileText, CheckCircle, Edit, Save, X, FileUp, AlertCircle, Loader2, 
  ChevronDown, ChevronUp, ExternalLink, FolderSearch, Package, Search,
  Building2, FileStack, ArrowLeft, Plus, Copy, Trash2, FilePlus, Eye, Palette,
  Download, ChevronLeft, ChevronRight, CalendarIcon
} from 'lucide-react';
import PDFViewer, { PDFIframe } from './PDFViewer';

// Wrapper that pre-fetches PDF via XHR as blob URL to avoid "Response body already used" clone errors
function BlobPDFViewer({ file, ...props }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);
  const urlRef = useRef(null);

  useEffect(() => {
    if (!file) return;
    // Revoke previous blob URL
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    setBlobUrl(null);
    setErr(null);

    const xhr = new XMLHttpRequest();
    xhr.open('GET', file, true);
    xhr.responseType = 'blob';
    // Include auth token for authenticated endpoints
    const token = localStorage.getItem('auth_token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const blob = xhr.response;
        const u = URL.createObjectURL(blob);
        urlRef.current = u;
        setBlobUrl(u);
      } else {
        setErr(`HTTP ${xhr.status}`);
      }
    };
    xhr.onerror = () => setErr('Network error');
    xhr.send();

    return () => { xhr.abort(); if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; } };
  }, [file]);

  if (err) return <div className="flex items-center justify-center py-8 text-red-500"><p>Error loading PDF: {err}</p></div>;
  if (!blobUrl) return <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /><span className="ml-2 text-gray-600">Loading PDF...</span></div>;
  return <PDFViewer file={blobUrl} {...props} />;
}

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api/subbuilder`;

// Built-in scope templates - FULL DATA (same as TemplatesStep.js)
const BUILTIN_SCOPE_TEMPLATES = {
  'aed-cabinets': {
    name: 'AED Cabinets',
    specSection: '10 43 13 Defibrillator Cabinets',
    otherTradesItems: ['Wall openings', 'Blocking', 'Confirm cabinet will fit in wall depth (depth info is provided in this submittal)', 'Caulking and/or backer rod'],
    scopeConcerns: [],
    fieldMeasurementItems: ['No field measurements are performed on this scope; if needed, trip charge may apply'],
    installationItems: ['Has final paint been completed'],
    isBuiltIn: true
  },
  'awl-lockers': {
    name: 'All Welded Lockers',
    specSection: '10 51 12.31 Welded Lockers',
    otherTradesItems: ['Backing - located at 8" centerline from bottom and 72" center (not AFF due to base)', 'Base by others unless otherwise noted', 'Locks - unless otherwise noted', "Power within 50' of locker area for compressor use if building lockers on site"],
    scopeConcerns: ['Confirm color selection - frames and doors, all interior colors to be standard for mfg. specifications', 'Confirm number sequencing. Note: if number plates begin with "00" there will be an additional fee', 'Confirm no radiant flooring or post tension rods to coordinate floor mounting with - if this is part of your project, x-ray work is by others', 'Identify locations of ADA required lockers in each elevation', 'Confirm wall outlets, light switches, strobe lights, etc. will be covered by lockers or slope top', 'Lock type if applicable'],
    fieldMeasurementItems: ['Base is installed; base to be plumb, level and clear of material & debris', 'Knock down lockers can be ordered without field measurements. Locker quantities may be reduced if opening is smaller than required clear opening. Allow 1/16" growth per frame', 'Knock down lockers utilize filler panels to fill gaps to walls, filler panels & splices cannot be cut less than 1-1/2" wide', 'If lockers are recessed with a soffit above, soffit depth must be equal to depth of lockers, soffit height is 1/2" to 2" above lockers finished height'],
    installationItems: ['A clear path must be provided for delivery of lockers. Lockers will be pre-built at ABS warehouse', 'In some cases lockers will be built on site, if that is to happen, ABS needs a clear location near the final locker destination for building lockers', 'If multiple floor installation; elevator or lift must be provided/available'],
    isBuiltIn: true
  },
  'bike-racks': {
    name: 'Bike Racks',
    specSection: '12 93 13 Indoor Bike Specialties',
    otherTradesItems: ['Blocking', 'If building is constructed with post tension rods, x-ray by others required before installation'],
    scopeConcerns: ['Confirm color'],
    fieldMeasurementItems: ['No field measurements are performed on this scope; if needed, trip charge may apply'],
    installationItems: ['Has final paint been completed'],
    isBuiltIn: true
  },
  'commercial-glass': {
    name: 'Commercial Glass',
    specSection: '08 40 00 Storefront/Entrances/Glazing',
    otherTradesItems: ['Wall openings', 'Blocking', 'Confirm there is no radiant flooring to coordinate floor mounting'],
    scopeConcerns: [],
    fieldMeasurementItems: ['Confirm date when all areas can be field measured by ABS'],
    installationItems: ['Confirm attic stock requirements per spec'],
    isBuiltIn: true
  },
  'fire-extinguishers': {
    name: 'Fire Protection',
    specSection: '10 44 16 Fire Extinguishers',
    otherTradesItems: ['Wall openings', 'Blocking', 'Confirm cabinet will fit in wall depth (depth info is provided in this submittal)', 'Caulking and/or backer rod'],
    scopeConcerns: ['All extinguishers are certified "new" out of the box, and do not require recertifications unless required by local inspector. If recertifications are required, this is excluded from Y/N'],
    fieldMeasurementItems: ['No field measurements are performed on this scope; if needed, trip charge may apply'],
    installationItems: ['Has final paint been completed'],
    isBuiltIn: true
  },
  'frp': {
    name: 'FRP Panels',
    specSection: '09 77 30 Fiberglass Reinforced Polyester Panels',
    otherTradesItems: ['Walls to be primed', 'Climate controlled building/rooms'],
    scopeConcerns: ['Building temperatures must remain at or above 65° - if they fall below, ABS will pull off of project. Additional trip charges may apply', 'Color selections confirmed', 'Sheet goods must be installed prior to sinks/plumbing fixtures/handles/large equipment', 'If floor to ceiling installation, sheet goods should be installed to ceiling grid/tile', 'Confirm trim requirements', 'Identify CMU locations - different adhesive will be required'],
    fieldMeasurementItems: ['No field measurements are performed on this scope; if needed, trip charge may apply'],
    installationItems: ['Surfaces must be taped, mudded, primed and smooth', 'Confirm no cabinets or other equipment has been installed', 'Schluter trim, tile and covebase must be installed', 'Screw inspections must be completed prior to installation of FRP'],
    isBuiltIn: true
  },
  'lockers': {
    name: 'Lockers',
    specSection: '10 55 00 Lockers',
    otherTradesItems: ['Floor must be level', 'Blocking for wall-mounted units'],
    scopeConcerns: ['Verify locker configuration', 'Confirm numbering sequence'],
    fieldMeasurementItems: ['Room dimensions verified', 'Electrical locations (if powered)'],
    installationItems: ['Flooring complete', 'Walls painted'],
    isBuiltIn: true
  },
  'mirrors': {
    name: 'Mirrors',
    specSection: '10 28 01 Mirrors',
    otherTradesItems: ['Wall openings', 'Blocking', 'Identify wainscot heights'],
    scopeConcerns: ['Confirm large frameless mirrors are by Division 08 Y/N'],
    fieldMeasurementItems: ['Drywall completed', 'Wall cutouts completed'],
    installationItems: ['Is there enough room between mirrors for soap dispensers?', 'Are countertops in place?'],
    isBuiltIn: true
  },
  'operable-wall': {
    name: 'Operable Partitions',
    specSection: '10 22 39 Operable Partitions',
    otherTradesItems: ['Wall openings', 'Blocking', 'Structural steel support required by others', 'Confirm color'],
    scopeConcerns: [],
    fieldMeasurementItems: ['Field measurements for panels will be taken by ABS at the time of track install'],
    installationItems: ['Confirm no M.E.P conflicts exist with op wall track', 'Panels may be shipped to job site. Please advise ABS if there will be access issues with getting the truck in or out of the parking lot. Additionally we will also need to have sidewalks poured and the asphalt laid to roll our carts over. Please advise if these requirements present any logistical problems'],
    isBuiltIn: true
  },
  'operable-wall-crown-door': {
    name: 'Operable Partitions - Crown Door',
    specSection: '10 22 26 Operable Partitions',
    otherTradesItems: ['Wall openings', 'Blocking', 'Structural steel support required by others', 'Confirm finish colors'],
    scopeConcerns: [],
    fieldMeasurementItems: ['Field conditions will supersede shop drawings, field dimensions are necessary for manufacturing precision. In the event that conditions change notify ABS', 'Confirm date when all areas can be field measured by ABS, this will be completed with installation of track; any additional trips will be an added trip charge'],
    installationItems: ['Confirm no M.E.P conflicts exist with op wall track'],
    isBuiltIn: true
  },
  'operable-wall-unispan': {
    name: 'Operable Partitions - UNISPAN',
    specSection: '10 22 26 Operable Partitions',
    otherTradesItems: ['Wall openings', 'Blocking', 'Select vinyl, marker board colors and trim colors'],
    scopeConcerns: [],
    fieldMeasurementItems: ['Confirm date when all areas can be field measured by ABS, this will be completed with installation of track; any additional trips will be an added trip charge'],
    installationItems: ['Confirm no M.E.P. conflicts exist with op wall track', 'Unispan posts will be installed outside finished walls, have walls prepared and final painted before delivery of Unispan'],
    isBuiltIn: true
  },
  'postal': {
    name: 'Postal Specialties',
    specSection: '10 55 00 Postal Specialties',
    otherTradesItems: ['Wall openings', 'Blocking'],
    scopeConcerns: ['Color selected', 'Provide number sequencing'],
    fieldMeasurementItems: ['Drywall completed', 'Wall cutouts completed'],
    installationItems: ['Has final paint been completed'],
    isBuiltIn: true
  },
  'site-furnishings': {
    name: 'Site Furnishings',
    specSection: '12 93 00 Site Furnishings',
    otherTradesItems: ['Wall openings', 'Blocking', 'Confirm color'],
    scopeConcerns: [],
    fieldMeasurementItems: [],
    installationItems: [],
    isBuiltIn: true
  },
  'smoke-fire-curtains': {
    name: 'Smoke & Fire Curtains',
    specSection: '08 33 43 Overhead Coiling Smoke Curtains for Elevator Doors',
    otherTradesItems: ['Wall openings', 'Blocking', '75% material deposit required before material can be released'],
    scopeConcerns: [],
    fieldMeasurementItems: ['No field measurements are performed on this scope; if needed, trip charge may apply'],
    installationItems: ['Has final paint been completed', 'Confirm attic stock requirements per spec'],
    isBuiltIn: true
  },
  'toilet-accessories': {
    name: 'Toilet Accessories',
    specSection: '10 28 00 Toilet Accessories',
    otherTradesItems: ['Blocking', 'All grab bar locations to have blocking'],
    scopeConcerns: ['Verify ADA compliance requirements', 'Confirm mounting heights with architect'],
    fieldMeasurementItems: ['Partition installation complete', 'Fixture rough-in complete'],
    installationItems: ['Walls must be finished', 'Tile work complete in wet areas'],
    isBuiltIn: true
  },
  'toilet-compartments': {
    name: 'Toilet Compartments',
    specSection: '10 21 13 Toilet Compartments & ADA Bench Items',
    otherTradesItems: ['Blocking', 'Ceiling supports by others', 'Confirm there is no radiant flooring to coordinate floor mounting with', 'Toilet fixtures to be installed prior to partition installation'],
    scopeConcerns: ['Confirm color selection', 'Identify any elevations that require child height'],
    fieldMeasurementItems: ['Framing and plumbing roughs set', 'If floor to ceiling installation - wood backing is required (suggestion is 2x6 backing)', 'If floor mounted partitions: confirm no radiant flooring in room', 'If ceiling hung partitions: ABS must arrive prior to ceiling being furnished to install ceiling bolts, then ceiling must be finished prior to panel installation'],
    installationItems: ['Permanent lighting required in restrooms prior to partitions installation (by others)', 'Restrooms to be available exclusively to ABS', 'Completion of wall & floor tile & grouting', 'Wall must be finished 24-48 hours prior', 'Final paint is complete', 'All ceiling work is complete', 'Countertops must be installed otherwise gaps may occur, ABS is not responsible if this condition is not met', 'Confirm no adjoining products interfere with pilaster installation at partial height tile wainscot & continuous brackets, if not addressed prior, ABS will not fill gaps (by others)', 'If tile is not fully covered with mortar/mastic, tiles will break when drilled. ABS is not responsible for broken tile'],
    isBuiltIn: true
  },
  'wall-protection': {
    name: 'Wall Protection',
    specSection: '10 26 00 Wall & Door Protection',
    otherTradesItems: ['Blocking', 'Handrail or any horizontal items must have continuous blocking', 'Walls to be primed'],
    scopeConcerns: ['All non-90 degree corners will require field measurements before material is ordered', 'Fire rated corner guards are furnished only and must be installed by drywall contractor'],
    fieldMeasurementItems: ['Completion of wainscot', 'Colors have been selected - if applicable'],
    installationItems: ["Handrail: provide dimensions at termination points. Door & window jamb, FEC's, corners, cased openings", 'Corner guards: cove base must be in place', 'Corner guards: walls must be painted', 'Clear corner guards: recommendation is to be back painted to match walls (by others)'],
    isBuiltIn: true
  },
  'wire-mesh-partitions': {
    name: 'Wire Mesh Partitions',
    specSection: '10 22 13 Wire Mesh Partitions',
    otherTradesItems: ['Blocking'],
    scopeConcerns: ['Confirm there are no radiant floors to coordinate floor mounting with', 'Confirm locker # sequence', 'Select color', 'Per NFP13: 18" vertical clearance without obstructions is required between the top of cage & ceilings. If shelves are on nearby wall, that is acceptable'],
    fieldMeasurementItems: ['Walled cages: confirm max wall span dimensions', 'Cages with roofs: confirm no ceiling cutouts required', 'Max roof size is 4\'x4\' or additional structure/ceiling supports are required', 'Roofs cannot be used with columns'],
    installationItems: ['Confirm attic stock requirements per spec'],
    isBuiltIn: true
  }
};

// Editable List Section Component (same as TemplatesStep.js)
function EditableListSection({ title, items, setItems, bgColor = '', disabled = false, showYesNoSelector = false, yesNoValue, onYesNoChange, allowPaste = false }) {
  const [pasteMode, setPasteMode] = useState(false);
  const addItem = () => setItems([...items, '']);
  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    setItems(newItems);
  };
  const removeItem = (index) => {
    if (items.length > 1) setItems(items.filter((_, i) => i !== index));
  };

  const handlePasteText = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length > 0) {
      const existing = items.filter(i => i.trim());
      setItems([...existing, ...lines]);
      setPasteMode(false);
    }
  };

  // Hide items when Y/N selector is present and value is "N"
  const showItems = !showYesNoSelector || yesNoValue !== 'N';

  return (
    <div className={`p-4 border-b border-gray-300 ${bgColor}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Label className="font-bold text-gray-900">{title}</Label>
          {showYesNoSelector && (
            <select
              value={yesNoValue || ''}
              onChange={(e) => onYesNoChange && onYesNoChange(e.target.value)}
              className="h-7 px-2 text-sm border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/50"
              disabled={disabled}
              data-testid="field-measurements-yn-selector"
            >
              <option value="">Select...</option>
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          )}
        </div>
        {!disabled && showItems && (
          <div className="flex items-center gap-2">
            {allowPaste && (
              <Button type="button" variant="outline" size="sm" onClick={() => setPasteMode(!pasteMode)} className="h-7 text-xs" data-testid="paste-mode-toggle">
                <FileText className="h-3 w-3 mr-1" /> Paste
              </Button>
            )}
            <Button type="button" variant="outline" size="sm" onClick={addItem} className="h-7 text-xs">
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
        )}
      </div>
      {allowPaste && pasteMode && (
        <div className="ml-4 mb-3 mt-2">
          <textarea
            className="w-full h-28 p-2 text-sm border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y"
            placeholder="Paste multiple items here (one per line)..."
            data-testid="paste-textarea"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                handlePasteText(e.target.value);
                e.target.value = '';
              }
            }}
          />
          <div className="flex items-center gap-2 mt-1">
            <Button type="button" variant="default" size="sm" className="h-7 text-xs" data-testid="paste-apply-btn"
              onClick={(e) => {
                const textarea = e.target.closest('div').parentElement.querySelector('textarea');
                handlePasteText(textarea.value);
                textarea.value = '';
              }}
            >
              Apply
            </Button>
            <span className="text-xs text-gray-500">Paste text, one item per line, then click Apply</span>
          </div>
        </div>
      )}
      {showItems && (
        <div className="space-y-2 ml-4">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 group">
              <span className="text-gray-600">-</span>
              <Input 
                value={item}
                onChange={(e) => updateItem(idx, e.target.value)}
                className="flex-1 h-8 text-sm text-gray-900"
                placeholder="Enter item..."
                disabled={disabled}
              />
              {!disabled && (
                <Button 
                  type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)}
                  className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50"
                  disabled={items.length <= 1}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {!showItems && (
        <p className="text-sm text-gray-500 italic ml-4">Not applicable when "No" is selected</p>
      )}
    </div>
  );
}

// Drag-Drop Icon Component (square with dashed border and arrow)
function DragDropIcon({ className = "h-4 w-4" }) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      {/* Solid square (file) */}
      <rect x="2" y="10" width="10" height="10" rx="1" />
      {/* Dashed square (drop zone) */}
      <rect x="12" y="4" width="10" height="10" rx="1" strokeDasharray="2 2" />
      {/* Arrow pointing from solid to dashed */}
      <path d="M9 13 L15 7" />
      <path d="M15 7 L15 11" />
      <path d="M15 7 L11 7" />
    </svg>
  );
}

// Drop Button - A button that also accepts drag & drop
function DropButton({ onFilesDropped, accept, multiple = false, disabled = false, isUploading = false, children, className = '', testId, size = 'default' }) {
  const fileInputRef = useRef(null);

  // Convert accept object to comma-separated string for native input
  // Always include .xlsm explicitly since browsers may not recognize its MIME type
  const acceptStr = accept ? [...new Set(Object.entries(accept).flatMap(([, exts]) => exts))].join(',') : undefined;

  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onFilesDropped(multiple ? files : files[0]);
    }
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onFilesDropped, multiple]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      onFilesDropped(multiple ? acceptedFiles : acceptedFiles[0]);
    }
  }, [onFilesDropped, multiple]);

  // Extend accept to also allow octet-stream for .xlsm
  const extendedAccept = accept ? {
    ...accept,
    'application/octet-stream': ['.xlsm', '.xls', '.xlsx'],
  } : undefined;

  const { getRootProps, isDragActive } = useDropzone({
    onDrop,
    accept: extendedAccept,
    validator: accept ? (file) => {
      const ext = '.' + (file.name || '').split('.').pop().toLowerCase();
      const allowed = Object.values(accept).flat();
      if (!allowed.includes(ext)) {
        return { code: 'file-invalid-type', message: `File type ${ext} not supported` };
      }
      return null;
    } : undefined,
    multiple,
    disabled: disabled || isUploading,
    noClick: true,
    noKeyboard: true
  });

  const sizeClasses = size === 'sm' ? 'h-9 px-3 text-xs' : 'h-10 px-4 py-2 text-sm';

  return (
    <div {...getRootProps()} className="inline-block">
      <label
        className={`
          inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium
          ring-offset-background transition-colors focus-visible:outline-none
          bg-primary text-primary-foreground hover:bg-primary/90
          ${sizeClasses} gap-1.5
          ${disabled || isUploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
          ${isDragActive ? 'ring-2 ring-primary ring-offset-2 scale-105' : ''}
          ${className}
        `}
        data-testid={testId}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptStr}
          multiple={multiple}
          disabled={disabled || isUploading}
          onChange={handleFileChange}
          className="sr-only"
        />
        {children}
      </label>
    </div>
  );
}

// Full-width drag-and-drop zone for FRS upload
function FrsDropZone({ transmittalId, index, isUploading, uploadProgress, onFileSelected }) {
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      onFileSelected(acceptedFiles[0]);
    }
  }, [onFileSelected]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'application/pdf': ['.pdf'],
      'application/octet-stream': ['.xlsm', '.xls', '.xlsx'],
    },
    validator: (file) => {
      const ext = '.' + (file.name || '').split('.').pop().toLowerCase();
      const allowed = ['.xlsx', '.xls', '.xlsm', '.docx', '.doc', '.pdf'];
      if (!allowed.includes(ext)) {
        return { code: 'file-invalid-type', message: `File type ${ext} not supported` };
      }
      return null;
    },
    multiple: false,
    disabled: isUploading,
    noClick: false
  });

  if (isUploading) {
    return (
      <div className="border-2 border-primary/30 bg-primary/5 rounded-lg p-4 text-center" data-testid={`frs-dropzone-${index}`}>
        <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mb-2" />
        <p className="text-sm font-medium text-primary">Uploading & Converting...</p>
        {uploadProgress > 0 && (
          <div className="mt-2 w-48 mx-auto bg-gray-200 rounded-full h-1.5">
            <div className="bg-primary h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={`
        border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-all duration-200
        ${isDragActive
          ? 'border-primary bg-primary/10 scale-[1.01]'
          : 'border-gray-600 hover:border-primary/60 hover:bg-primary/5'
        }
      `}
      data-testid={`frs-dropzone-${index}`}
    >
      <input {...getInputProps()} />
      <Upload className={`h-8 w-8 mx-auto mb-2 ${isDragActive ? 'text-primary' : 'text-gray-400'}`} />
      {isDragActive ? (
        <p className="text-sm font-medium text-primary">Drop your file here</p>
      ) : (
        <>
          <p className="text-sm font-medium text-gray-300">
            Drag & drop your FRS file here, or <span className="text-primary underline">click to browse</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">Supports .xlsx, .xls, .xlsm, .docx, .doc, .pdf</p>
        </>
      )}
    </div>
  );
}

export default function UploadStep({ project, selectedTemplates = [], setSelectedTemplates, transmittalsProgress = {}, pendingFrsFile, onPendingFrsConsumed, onComplete, onProjectUpdate, onUsedTemplatesChange }) {
  const [transmittals, setTransmittals] = useState([]);
  const [allTemplates, setAllTemplates] = useState({ ...BUILTIN_SCOPE_TEMPLATES });
  const [expandedTransmittal, setExpandedTransmittal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buildingPackage, setBuildingPackage] = useState(null);
  const [buildComplete, setBuildComplete] = useState(null); // transmittalId when just finished
  const [savingSubmittal, setSavingSubmittal] = useState(false);
  const [uploadingMissingItem, setUploadingMissingItem] = useState(null); // tracks "transmittalId|model" key
  const [manualUploadProgress, setManualUploadProgress] = useState({}); // { transmittalId: { progress: 0, type: 'color_card' } }
  const isLoadingRef = useRef(false); // Prevent duplicate loadOrCreateSubmittals calls
  const pendingReloadRef = useRef(false); // Re-run requested while a load was in flight
  
  // Manual manufacturer search state
  const [mfgSearchInput, setMfgSearchInput] = useState({}); // { "transmittalId|productIndex": "manufacturer name" }
  const [mfgSearching, setMfgSearching] = useState(null); // "transmittalId|productIndex" being searched
  const [mfgSearchResults, setMfgSearchResults] = useState({}); // { "transmittalId|productIndex": { documents: [], message: "" } }
  
  // Editing state - job-specific editing (saves to database)
  const [editingTransmittalId, setEditingTransmittalId] = useState(null);
  const [editMode, setEditMode] = useState(null); // 'edit' | 'view'
  
  // Edit form state
  const [templateName, setTemplateName] = useState('');
  const [specSection, setSpecSection] = useState('');
  const [otherTradesItems, setOtherTradesItems] = useState(['']);
  const [scopeConcerns, setScopeConcerns] = useState(['']);
  const [fieldMeasurementItems, setFieldMeasurementItems] = useState(['']);
  const [fieldMeasurementRequired, setFieldMeasurementRequired] = useState('');
  const [installationItems, setInstallationItems] = useState(['']);
  const [installationPrepRequired, setInstallationPrepRequired] = useState('');
  const [atticStockRequired, setAtticStockRequired] = useState('');
  const [atticStockItems, setAtticStockItems] = useState(['']);
  const [proposalExclusions, setProposalExclusions] = useState(['']);
  const [leadTime, setLeadTime] = useState('');
  const [shippingTime, setShippingTime] = useState('');
  const [installDuration, setInstallDuration] = useState('');
  const [returnSubmittalDue, setReturnSubmittalDue] = useState('');
  const [planDate, setPlanDate] = useState('');
  const [submittalNo, setSubmittalNo] = useState('');
  const [dateSubmitted, setDateSubmitted] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const DEFAULT_ITEMS_ATTACHED = {
    product_data: true, shop_drawings: true, color_selectors: true,
    leed_docs: false, warranty_samples: false, quantity_takeoff: false,
    msds_sheets: false, other1_checked: false, other1_text: '',
    other2_checked: false, other2_text: ''
  };
  const [itemsAttached, setItemsAttached] = useState(DEFAULT_ITEMS_ATTACHED);
  
  // Compiled PDF state
  const [compilingPdf, setCompilingPdf] = useState({});
  const [compiledPdfs, setCompiledPdfs] = useState({});
  
  // View submittal state
  const [viewingSubmittal, setViewingSubmittal] = useState(null);
  
  // Color options state
  const [colorOptionsOpen, setColorOptionsOpen] = useState({});

  // Load custom templates and merge with built-in
  useEffect(() => {
    loadCustomTemplates();
  }, []);

  const loadCustomTemplates = async () => {
    try {
      const response = await axios.get(`${API}/scope-templates`);
      if (response.data.templates) {
        const customObj = {};
        response.data.templates.forEach(t => {
          const key = t.overridesBuiltIn || t.id;
          customObj[key] = {
            id: t.id,
            name: t.name,
            specSection: t.specSection,
            otherTradesItems: t.otherTradesItems || [],
            scopeConcerns: t.scopeConcerns || [],
            fieldMeasurementItems: t.fieldMeasurementItems || [],
            fieldMeasurementRequired: t.fieldMeasurementRequired || '',
            installationItems: t.installationItems || [],
            installationPrepRequired: t.installationPrepRequired || '',
            atticStockRequired: t.atticStockRequired || '',
            isCustom: true,
            overridesBuiltIn: t.overridesBuiltIn || null
          };
        });
        
        // Merge - custom overrides built-in
        const merged = { ...BUILTIN_SCOPE_TEMPLATES };
        Object.keys(customObj).forEach(key => {
          merged[key] = customObj[key];
        });
        setAllTemplates(merged);
      }
    } catch (error) {
      console.error('Error loading custom templates:', error);
    }
  };

  // Notify parent when transmittals change (for filtering in TemplatesStep)
  useEffect(() => {
    if (onUsedTemplatesChange && transmittals.length > 0) {
      const usedIds = transmittals.map(t => t.templateId || t.template_id).filter(Boolean);
      onUsedTemplatesChange(usedIds);
    }
  }, [transmittals, onUsedTemplatesChange]);

  // Stabilize selectedTemplates dependency to prevent unnecessary re-runs from array reference changes
  const selectedTemplatesKey = JSON.stringify(selectedTemplates || []);

  // Load persistent submittals from database OR create them from selected templates
  useEffect(() => {
    if (project?.id) {
      loadOrCreateSubmittals();
    }
    // Note: allTemplates is intentionally excluded - the function handles template lookup internally
    // selectedTemplatesKey is used instead of selectedTemplates to avoid array reference triggers
  }, [project?.id, selectedTemplatesKey]);

  // Auto-upload FRS file from TemplatesStep (passed via pendingFrsFile prop)
  const pendingFrsConsumedRef = useRef(false);
  useEffect(() => {
    if (!pendingFrsFile || pendingFrsConsumedRef.current) return;
    if (transmittals.length === 0) return;
    // Only trigger once when transmittals are loaded
    const firstNotUploaded = transmittals.find(t => !t.uploaded);
    if (!firstNotUploaded) return;
    pendingFrsConsumedRef.current = true;
    toast.info('Auto-uploading FRS from template matching...', { duration: 5000 });
    handleUploadFRS(firstNotUploaded.id, pendingFrsFile);
    if (onPendingFrsConsumed) onPendingFrsConsumed();
  }, [pendingFrsFile, transmittals]);


  // Helper to convert a DB submittal to frontend UI state
  const toSubmittalUIState = (s, isNew = false) => ({
    ...s,
    templateId: s.template_id,
    otherTradesItems: s.other_trades_items || [],
    scopeConcerns: s.scope_concerns || [],
    fieldMeasurementItems: s.field_measurement_items || [],
    fieldMeasurementRequired: s.field_measurement_required || '',
    installationItems: s.installation_items || [],
    installationPrepRequired: s.installation_prep_required || '',
    atticStockRequired: s.attic_stock_required || '',
    atticStockItems: s.attic_stock_items || [],
    itemsAttached: s.items_attached || null,
    proposalExclusions: s.proposal_exclusions || [],
    frsFilePath: s.frs_file_path,
    frsUploaded: s.frs_uploaded,
    parsedData: s.parsed_data,
    vendorDocs: (() => { try { const vd = s.vendor_docs; return Array.isArray(vd) ? vd : (typeof vd === 'string' ? JSON.parse(vd) : []); } catch { return []; } })(),
    manualDocs: (() => { try { const md = s.manual_docs; return Array.isArray(md) ? md : (typeof md === 'string' ? JSON.parse(md) : []); } catch { return []; } })(),
    missingItems: (() => { try { const mi = s.missing_items; return Array.isArray(mi) ? mi : (typeof mi === 'string' ? JSON.parse(mi) : []); } catch { return []; } })(),
    specSection: s.spec_section,
    frsFile: null,
    uploading: false,
    uploadProgress: 0,
    uploaded: isNew ? false : (s.frs_uploaded || false),
    lineItems: isNew ? [] : (s.parsed_data?.line_items || []),
    createdSubmittals: [],
    pendingManualDocs: [],
    uploadingManualDocs: false,
    searchingDocs: false,
    docsSearched: false,
    error: null
  });

  // Build a complete template lookup (builtins + custom via API if needed)
  const buildTemplateLookup = async (templateIds) => {
    const lookup = { ...BUILTIN_SCOPE_TEMPLATES, ...allTemplates };
    
    const missingIds = templateIds.filter(id =>
      !lookup[id] && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
    );
    
    if (missingIds.length > 0) {
      try {
        const resp = await axios.get(`${API}/scope-templates`);
        (resp.data.templates || []).forEach(t => {
          lookup[t.id] = {
            id: t.id, name: t.name, specSection: t.specSection,
            otherTradesItems: t.otherTradesItems || [], scopeConcerns: t.scopeConcerns || [],
            fieldMeasurementItems: t.fieldMeasurementItems || [],
            fieldMeasurementRequired: t.fieldMeasurementRequired || '',
            installationItems: t.installationItems || [],
            installationPrepRequired: t.installationPrepRequired || '',
            atticStockRequired: t.atticStockRequired || '',
            isCustom: true
          };
        });
      } catch (err) {
        console.error('Error fetching templates for lookup:', err);
      }
    }
    return lookup;
  };

  const loadOrCreateSubmittals = async () => {
    if (!project?.id) return;
    
    // Prevent concurrent calls; queue a re-run so late selection changes aren't dropped
    if (isLoadingRef.current) {
      pendingReloadRef.current = true;
      return;
    }
    isLoadingRef.current = true;
    setLoading(true);
    
    try {
      // Load existing submittals from database
      const response = await axios.get(`${API}/projects/${project.id}/project-submittals`);
      const existingSubmittals = response.data.submittals || [];
      
      if (existingSubmittals.length > 0) {
        const existingTemplateIds = existingSubmittals.map(s => s.template_id);
        const newTemplateIds = (selectedTemplates || []).filter(id => !existingTemplateIds.includes(id));
        const submittalsWithState = existingSubmittals.map(s => toSubmittalUIState(s));
        
        // Auto-pull product data for auto-created submittals (FRS parsed but package never built)
        autoBuildPendingSubmittals(submittalsWithState);
        
        if (newTemplateIds.length > 0) {
          const templateLookup = await buildTemplateLookup(newTemplateIds);
          const submittalsToCreate = newTemplateIds.map(templateId => {
            const template = templateLookup[templateId] || {};
            return {
              template_id: templateId, name: template.name || templateId,
              spec_section: template.specSection || '',
              other_trades_items: template.otherTradesItems || [],
              scope_concerns: template.scopeConcerns || [],
              field_measurement_items: template.fieldMeasurementItems || [],
              field_measurement_required: template.fieldMeasurementRequired || '',
              installation_items: template.installationItems || [],
              installation_prep_required: template.installationPrepRequired || '',
              attic_stock_required: template.atticStockRequired || '',
              attic_stock_items: template.atticStockItems || [],
              items_attached: template.itemsAttached || {},
              lead_time: template.leadTime || '',
              shipping_time: template.shippingTime || '',
              install_duration: template.installDuration || '',
              return_submittal_due: template.returnSubmittalDue || '',
              plan_date: template.planDate || '',
              additional_info: template.additionalInfo || ''
            };
          });
          
          try {
            const createResponse = await axios.post(
              `${API}/projects/${project.id}/project-submittals/bulk-create`,
              submittalsToCreate
            );
            if (createResponse.data.success) {
              const newSubmittals = (createResponse.data.submittals || []).map(s => toSubmittalUIState(s, true));
              setTransmittals([...submittalsWithState, ...newSubmittals]);
              toast.success(`Added ${newSubmittals.length} new submittal(s)`);
              return;
            }
          } catch (err) {
            console.error('Error creating new submittals:', err);
          }
        }
        
        setTransmittals(submittalsWithState);
        return;
      }
      
      // No existing submittals - create from selected templates
      if (selectedTemplates && selectedTemplates.length > 0) {
        const templateLookup = await buildTemplateLookup(selectedTemplates);
        const submittalsToCreate = selectedTemplates.map(templateId => {
          const template = templateLookup[templateId] || {};
          return {
            template_id: templateId, name: template.name || templateId,
            spec_section: template.specSection || '',
            other_trades_items: template.otherTradesItems || [],
            scope_concerns: template.scopeConcerns || [],
            field_measurement_items: template.fieldMeasurementItems || [],
            field_measurement_required: template.fieldMeasurementRequired || '',
            installation_items: template.installationItems || [],
            installation_prep_required: template.installationPrepRequired || '',
            attic_stock_required: template.atticStockRequired || '',
            attic_stock_items: template.atticStockItems || [],
            items_attached: template.itemsAttached || {},
            lead_time: template.leadTime || '',
            shipping_time: template.shippingTime || '',
            install_duration: template.installDuration || '',
            return_submittal_due: template.returnSubmittalDue || '',
            plan_date: template.planDate || '',
            additional_info: template.additionalInfo || ''
          };
        });
        
        const createResponse = await axios.post(
          `${API}/projects/${project.id}/project-submittals/bulk-create`,
          submittalsToCreate
        );
        
        if (createResponse.data.success) {
          const created = (createResponse.data.submittals || []).map(s => toSubmittalUIState(s, true));
          setTransmittals(created);
          toast.success(`Created ${created.length} submittals for this project`);
        }
      }
    } catch (error) {
      console.error('Error loading/creating submittals:', error);
      toast.error('Failed to load submittals');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      if (pendingReloadRef.current) {
        pendingReloadRef.current = false;
        loadOrCreateSubmittals();
      }
    }
  };

  // Open submittal for editing (job-specific, saves to database)
  const handleOpenEdit = (transmittalId) => {
    const transmittal = transmittals.find(t => t.id === transmittalId);
    if (transmittal) {
      setEditingTransmittalId(transmittalId);
      setEditMode('edit');
      setTemplateName(transmittal.name || '');
      setSpecSection(transmittal.specSection || '');
      setOtherTradesItems([...(transmittal.otherTradesItems?.length ? transmittal.otherTradesItems : [''])]);
      setScopeConcerns([...(transmittal.scopeConcerns?.length ? transmittal.scopeConcerns : [''])]);
      setFieldMeasurementItems([...(transmittal.fieldMeasurementItems?.length ? transmittal.fieldMeasurementItems : [''])]);
      setFieldMeasurementRequired(transmittal.fieldMeasurementRequired || '');
      setInstallationItems([...(transmittal.installationItems?.length ? transmittal.installationItems : [''])]);
      setInstallationPrepRequired(transmittal.installationPrepRequired || '');
      setAtticStockRequired(transmittal.atticStockRequired || '');
      setAtticStockItems([...(transmittal.atticStockItems?.length ? transmittal.atticStockItems : [''])]);
      setProposalExclusions([...(transmittal.proposalExclusions?.length ? transmittal.proposalExclusions : [''])]);
      setItemsAttached(transmittal.itemsAttached || DEFAULT_ITEMS_ATTACHED);
      // Smart defaults for transmittal info
      const specNum = (transmittal.specSection || transmittal.spec_section || '').replace(/\s+/g, '').replace(/[^0-9]/g, '');
      const defaultSubmittalNo = specNum ? `${specNum}-1.0` : '';
      const today = new Date();
      const todayStr = format(today, 'MM/dd/yyyy');
      const threeWeeksOut = new Date(today);
      threeWeeksOut.setDate(threeWeeksOut.getDate() + 21);
      const threeWeeksStr = format(threeWeeksOut, 'MM/dd/yyyy');

      setSubmittalNo(transmittal.submittalNo || transmittal.submittal_no || defaultSubmittalNo);
      setDateSubmitted(transmittal.dateSubmitted || transmittal.date_submitted || todayStr);
      setReturnSubmittalDue(transmittal.returnSubmittalDue || transmittal.return_submittal_due || threeWeeksStr);
      setShippingTime(transmittal.shippingTime || transmittal.shipping_time || '1 Week');
      setLeadTime(transmittal.leadTime || transmittal.lead_time || '');
      setInstallDuration(transmittal.installDuration || transmittal.install_duration || '');
      setPlanDate(transmittal.planDate || transmittal.plan_date || '');
      setAdditionalInfo(transmittal.additionalInfo || transmittal.additional_info || '');
    }
  };

  // Save submittal changes to database - PERSISTENT (saves to backend)
  const handleSaveSubmittal = async () => {
    if (!templateName.trim() || !specSection.trim()) {
      toast.error('Please enter name and spec section');
      return;
    }

    setSavingSubmittal(true);
    try {
      const updateData = {
        name: templateName.trim(),
        spec_section: specSection.trim(),
        other_trades_items: otherTradesItems.filter(i => i.trim()),
        scope_concerns: scopeConcerns.filter(i => i.trim()),
        field_measurement_items: fieldMeasurementItems.filter(i => i.trim()),
        field_measurement_required: fieldMeasurementRequired,
        installation_items: installationItems.filter(i => i.trim()),
        installation_prep_required: installationPrepRequired,
        attic_stock_required: atticStockRequired,
        attic_stock_items: atticStockItems.filter(i => i.trim()),
        proposal_exclusions: proposalExclusions.filter(i => i.trim()),
        items_attached: itemsAttached,
        lead_time: leadTime === 'custom' ? '' : leadTime,
        shipping_time: shippingTime === 'custom' ? '' : shippingTime,
        install_duration: installDuration,
        return_submittal_due: returnSubmittalDue,
        plan_date: planDate,
        submittal_no: submittalNo,
        date_submitted: dateSubmitted,
        additional_info: additionalInfo
      };

      const response = await axios.put(
        `${API}/projects/${project.id}/project-submittals/${editingTransmittalId}`,
        updateData
      );

      if (response.data.success) {
        // Update local state with saved data
        setTransmittals(prev => prev.map(t => 
          t.id === editingTransmittalId ? {
            ...t,
            name: templateName.trim(),
            specSection: specSection.trim(),
            otherTradesItems: otherTradesItems.filter(i => i.trim()),
            scopeConcerns: scopeConcerns.filter(i => i.trim()),
            fieldMeasurementItems: fieldMeasurementItems.filter(i => i.trim()),
            fieldMeasurementRequired: fieldMeasurementRequired,
            installationItems: installationItems.filter(i => i.trim()),
            installationPrepRequired: installationPrepRequired,
            atticStockRequired: atticStockRequired,
            atticStockItems: atticStockItems.filter(i => i.trim()),
            proposalExclusions: proposalExclusions.filter(i => i.trim()),
            itemsAttached: itemsAttached,
            leadTime: leadTime,
            shippingTime: shippingTime,
            installDuration: installDuration,
            returnSubmittalDue: returnSubmittalDue,
            planDate: planDate,
            submittalNo: submittalNo,
            dateSubmitted: dateSubmitted,
            additionalInfo: additionalInfo
          } : t
        ));

        toast.success('Submittal saved successfully');
        setEditingTransmittalId(null);
        setEditMode(null);

        // Auto-rebuild package if FRS was uploaded (so View reflects updated transmittal info)
        const transmittal = transmittals.find(t => t.id === editingTransmittalId);
        if (transmittal && (transmittal.uploaded || transmittal.frsUploaded)) {
          toast.info('Rebuilding submittal package with updated info...', { duration: 8000 });
          handleBuildPackage(editingTransmittalId);
        }
      }
    } catch (error) {
      console.error('Error saving submittal:', error);
      toast.error('Failed to save submittal');
    } finally {
      setSavingSubmittal(false);
    }
  };

  // Remove submittal from project - immediate, no confirmation
  const handleDeleteSubmittal = async (transmittalId) => {
    const transmittal = transmittals.find(t => t.id === transmittalId);
    if (!transmittal) return;

    try {
      const response = await axios.delete(
        `${API}/projects/${project.id}/project-submittals/${transmittalId}`
      );

      if (response.data.success) {
        setTransmittals(prev => prev.filter(t => t.id !== transmittalId));
        
        // Remove from selectedTemplates to prevent re-creation when navigating back
        if (setSelectedTemplates && transmittal.templateId) {
          setSelectedTemplates(prev => prev.filter(id => id !== transmittal.templateId));
        }
        
        // Also update usedTemplateIds to allow re-selection from Transmittals tab
        if (onUsedTemplatesChange && transmittal.templateId) {
          onUsedTemplatesChange(prev => prev.filter(id => id !== transmittal.templateId));
        }
        
        toast.success(`"${transmittal.name}" removed from project.`);
      }
    } catch (error) {
      console.error('Error removing submittal:', error);
      toast.error('Failed to remove submittal');
    }
  };

  // Create another transmittal of the same scope
  const [duplicatingId, setDuplicatingId] = useState(null);
  const handleDuplicateSubmittal = async (transmittalId) => {
    setDuplicatingId(transmittalId);
    try {
      const response = await axios.post(
        `${API}/projects/${project.id}/project-submittals/${transmittalId}/duplicate`
      );
      if (response.data.success && response.data.submittal) {
        const newSub = toSubmittalUIState(response.data.submittal);
        setTransmittals(prev => [...prev, newSub]);
        toast.success(`Created "${newSub.name}"`);
      }
    } catch (error) {
      console.error('Error duplicating submittal:', error);
      toast.error('Failed to duplicate submittal');
    } finally {
      setDuplicatingId(null);
    }
  };

  // Auto-pull product data for submittals created via FRS auto-project
  const autoBuildTriggeredRef = useRef(false);
  const autoBuildPendingSubmittals = (subs) => {
    if (autoBuildTriggeredRef.current) return;
    const pending = subs.filter(t =>
      (t.frsUploaded || t.frs_uploaded) &&
      (t.parsedData?.line_items?.length > 0) &&
      !(t.vendorDocs?.length) &&
      !t.packageUrl && !t.package_url &&
      t.status !== 'package_built'
    );
    if (pending.length === 0) return;
    autoBuildTriggeredRef.current = true;
    toast.info(`Pulling product data from SharePoint for ${pending.length} submittal(s)...`, { duration: 12000 });
    let delay = 800;
    for (const t of pending) {
      setTimeout(() => handleBuildPackage(t.id), delay);
      delay += 1500; // Stagger to avoid overwhelming SharePoint
    }
  };

  // Build Submittal Package - fetches product data sheets from SharePoint and assembles PDF
  const handleBuildPackage = async (transmittalId) => {    setBuildingPackage(transmittalId);
    toast.info('Building submittal package from SharePoint...', { duration: 10000 });
    try {
      const response = await axios.post(
        `${API}/projects/${project.id}/project-submittals/${transmittalId}/build-package`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      const data = response.data;
      if (data.success) {
        toast.success(`Package built: ${data.matched}/${data.total_products} products matched`);
        if (data.unmatched > 0) {
          toast.info(`${data.unmatched} product(s) could not be found on SharePoint`);
        }
        // Update the transmittal with vendor docs, package URL, and missing items
        setTransmittals(prev => prev.map(t =>
          t.id === transmittalId ? {
            ...t,
            vendorDocs: data.parts || [],
            packageUrl: data.package_url,
            missingItems: data.unmatched_details || [],
            status: 'package_built'
          } : t
        ));
        // Show "Build Complete" status line briefly
        setBuildComplete(transmittalId);
        setTimeout(() => setBuildComplete(null), 4000);
      }
    } catch (error) {
      console.error('Error building package:', error);
      const detail = error.response?.data?.detail || 'Failed to build submittal package';
      toast.error(detail);
    } finally {
      setBuildingPackage(null);
    }
  };


  // Append a file (product data or color card) to existing package
  const handleAppendToPackage = async (transmittalId, files, docType = 'product_data') => {
    const filesArray = Array.isArray(files) ? files : [files];
    setTransmittals(prev => prev.map(t =>
      t.id === transmittalId ? { ...t, uploadingManualDocs: true } : t
    ));
    setManualUploadProgress(prev => ({ ...prev, [transmittalId]: { progress: 0, type: docType, fileName: filesArray[0]?.name || '' } }));
    try {
      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('doc_type', docType);
        const response = await axios.post(
          `${API}/projects/${project.id}/project-submittals/${transmittalId}/append-to-package`,
          formData,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const fileProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              const overallProgress = Math.round(((i * 100) + fileProgress) / filesArray.length);
              setManualUploadProgress(prev => ({ ...prev, [transmittalId]: { progress: overallProgress, type: docType, fileName: file.name } }));
            }
          }
        );
        if (response.data.success) {
          setTransmittals(prev => prev.map(t =>
            t.id === transmittalId ? {
              ...t,
              packageUrl: response.data.package_url,
              vendorDocs: response.data.parts || t.vendorDocs
            } : t
          ));
          toast.success(response.data.message);
        }
      }
    } catch (error) {
      console.error('Error appending to package:', error);
      toast.error(error.response?.data?.detail || 'Failed to add to package');
    } finally {
      setManualUploadProgress(prev => ({ ...prev, [transmittalId]: { progress: 100, type: docType, fileName: '' } }));
      setTimeout(() => {
        setManualUploadProgress(prev => { const n = { ...prev }; delete n[transmittalId]; return n; });
      }, 1500);
      setTransmittals(prev => prev.map(t =>
        t.id === transmittalId ? { ...t, uploadingManualDocs: false } : t
      ));
    }
  };


  // Upload a PDF for a specific missing product — inserts at correct position in package
  const handleUploadMissingDoc = async (transmittalId, missingItem, file) => {
    const itemKey = `${transmittalId}|${missingItem.model}`;
    setUploadingMissingItem(itemKey);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('manufacturer', missingItem.manufacturer || '');
      formData.append('model', missingItem.model || '');
      formData.append('product_index', missingItem.product_index ?? -1);

      const response = await axios.post(
        `${API}/projects/${project.id}/project-submittals/${transmittalId}/upload-missing-doc`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        // Update transmittal state: remove from missing, update parts + package URL
        setTransmittals(prev => prev.map(t =>
          t.id === transmittalId ? {
            ...t,
            missingItems: response.data.missing_items || [],
            vendorDocs: response.data.parts || t.vendorDocs,
            packageUrl: response.data.package_url || t.packageUrl,
          } : t
        ));
        toast.success(`Uploaded ${missingItem.manufacturer} ${missingItem.model} — inserted in correct position`);
      }
    } catch (error) {
      console.error('Error uploading missing doc:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload missing document');
    } finally {
      setUploadingMissingItem(null);
    }
  };

  // Manual manufacturer search on SharePoint
  const handleSearchManufacturer = async (transmittalId, item, searchKey) => {
    const mfgName = (mfgSearchInput[searchKey] || '').trim();
    if (!mfgName) {
      toast.error('Please enter a manufacturer name');
      return;
    }
    setMfgSearching(searchKey);
    try {
      const formData = new FormData();
      formData.append('manufacturer', mfgName);
      formData.append('model', item.model || '');
      formData.append('product_index', item.product_index ?? -1);
      const response = await axios.post(
        `${API}/projects/${project.id}/project-submittals/${transmittalId}/search-manufacturer`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      setMfgSearchResults(prev => ({ ...prev, [searchKey]: response.data }));
      if (response.data.success && response.data.documents?.length > 0) {
        toast.success(response.data.message);
      } else {
        toast.info(response.data.message || 'No documents found');
      }
    } catch (error) {
      console.error('Manufacturer search error:', error);
      toast.error('Search failed — SharePoint may not be configured');
      setMfgSearchResults(prev => ({ ...prev, [searchKey]: { success: false, documents: [], message: 'Search failed' } }));
    } finally {
      setMfgSearching(null);
    }
  };

  // Download a found SharePoint document and attach to submittal
  const handleAttachSearchResult = async (transmittalId, item, doc) => {
    const searchKey = `${transmittalId}|${item.product_index ?? item.model}`;
    setMfgSearching(searchKey);
    try {
      // Download the file from SharePoint and upload as missing doc
      const dlUrl = doc.download_url;
      if (!dlUrl) {
        toast.error('No download URL available for this document');
        return;
      }
      const resp = await fetch(dlUrl);
      const blob = await resp.blob();
      const file = new File([blob], doc.name, { type: 'application/pdf' });
      await handleUploadMissingDoc(transmittalId, { ...item, manufacturer: mfgSearchInput[searchKey] || item.manufacturer }, file);
      // Clear search results after successful attach
      setMfgSearchResults(prev => {
        const next = { ...prev };
        delete next[searchKey];
        return next;
      });
    } catch (error) {
      console.error('Error attaching document:', error);
      toast.error('Failed to attach document');
    } finally {
      setMfgSearching(null);
    }
  };



  // Handle Proposal Upload - Parse and auto-create submittals with exclusions
  // Final Room Schedule Upload handlers
  const handleFileSelect = (transmittalId, file) => {
    setTransmittals(prev => prev.map(t => 
      t.id === transmittalId ? { ...t, frsFile: file, error: null } : t
    ));
  };

  // Clear/Delete Final Room Schedule upload - resets to allow re-upload
  const handleClearFRS = async (transmittalId) => {
    // ONLY clear FRS-specific data. Never touch transmittal info.
    try {
      await axios.put(
        `${API}/projects/${project.id}/project-submittals/${transmittalId}`,
        {
          frs_uploaded: false,
          frs_file_path: null,
          parsed_data: null,
          vendor_docs: [],
          missing_items: []
        }
      );
    } catch (error) {
      console.error('Error clearing FRS in database:', error);
    }

    setTransmittals(prev => prev.map(t => 
      t.id === transmittalId ? { 
        ...t, 
        frsFile: null,
        uploading: false,
        uploadProgress: 0,
        uploaded: false,
        parsedData: null,
        lineItems: [],
        vendorDocs: [],
        missingItems: [],
        searchingDocs: false,
        docsSearched: false,
        error: null
      } : t
    ));
    toast.success('FRS cleared. Transmittal info preserved.');
  };

  const handleUploadFRS = async (transmittalId, directFile = null) => {
    const transmittal = transmittals.find(t => t.id === transmittalId);
    const fileToUpload = directFile || transmittal?.frsFile;
    
    if (!fileToUpload) {
      toast.error('Please select a file');
      return;
    }

    setTransmittals(prev => prev.map(t => 
      t.id === transmittalId ? { ...t, uploading: true, uploadProgress: 0, error: null, frsFile: fileToUpload } : t
    ));

    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('project_id', project?.id || 'demo-project');

      const response = await axios.post(`${API}/upload/frs`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setTransmittals(prev => prev.map(t => 
            t.id === transmittalId ? { ...t, uploadProgress: progress } : t
          ));
        }
      });

      if (response.data.success) {
        const parsedData = response.data.parsed_data || response.data.parsedData;
        const lineItems = parsedData?.line_items || [];
        const createdSubmittals = response.data.submittals || [];
        const filePath = response.data.file_path;  // Get the Excel file path from response
        const frsPdfPath = response.data.frs_pdf_path;  // Get the primary PDF path
        const scopePdfs = response.data.scope_pdfs || {};  // Get scope-specific PDFs
        
        // Helper: filter parsed_data to only include items for a specific transmittal's spec section
        const filterParsedDataForTransmittal = (t) => {
          if (!parsedData) return parsedData;
          const tSpec = (t.specSection || t.spec_section || '').replace(/[^0-9]/g, '').substring(0, 6);
          const tPrefix = tSpec.substring(0, 4);
          if (!tPrefix || tPrefix.length < 4) return parsedData;
          // Strict: only keep items whose spec_section prefix matches (no pass-through for missing spec)
          const filteredItems = lineItems.filter(item => {
            const itemSpec = (item.spec_section || '').replace(/[^0-9]/g, '').substring(0, 6);
            const itemPrefix = itemSpec.substring(0, 4);
            return itemPrefix.length >= 4 && itemPrefix === tPrefix;
          });
          const filteredScopes = {};
          for (const [key, val] of Object.entries(parsedData.scopes || {})) {
            const scopeSpec = key.replace(/[^0-9]/g, '').substring(0, 6);
            const scopePrefix = scopeSpec.substring(0, 4);
            if (scopePrefix.length >= 4 && scopePrefix === tPrefix) filteredScopes[key] = val;
          }
          // Also filter spec_sections array to only matching sections
          const filteredSpecSections = (parsedData.spec_sections || []).filter(section => {
            const secSpec = (section.spec_section || '').replace(/[^0-9]/g, '').substring(0, 6);
            const secPrefix = secSpec.substring(0, 4);
            return secPrefix.length >= 4 && secPrefix === tPrefix;
          });
          return { ...parsedData, line_items: filteredItems, scopes: filteredScopes, spec_sections: filteredSpecSections };
        };
        
        // scope_pdfs entries are now {pdf_path, spec_section, name} objects
        // Helper: extract pdf_path from new or legacy format
        const getScopePdfPath = (entry) => {
          if (!entry) return null;
          if (typeof entry === 'string') return entry; // Legacy format
          return entry.pdf_path || null;
        };
        const getScopeSpec = (entry) => {
          if (!entry || typeof entry === 'string') return '';
          return (entry.spec_section || '').replace(/[^0-9]/g, '').substring(0, 6);
        };
        
        // Helper: match a scope_pdf entry to a transmittal by spec_section (4-digit prefix)
        const scopeMatchesTransmittal = (scopeEntry, t) => {
          const scopeSpec = getScopeSpec(scopeEntry);
          const scopePrefix = scopeSpec.substring(0, 4);
          const tSpec = (t.specSection || t.spec_section || '').replace(/[^0-9]/g, '').substring(0, 6);
          const tPrefix = tSpec.substring(0, 4);
          if (scopePrefix.length >= 4 && tPrefix.length >= 4 && scopePrefix === tPrefix) return true;
          return false;
        };
        
        // Get the current transmittal to find its template_id
        const currentTransmittal = transmittals.find(t => t.id === transmittalId);
        const templateId = currentTransmittal?.templateId || currentTransmittal?.template_id;
        
        // Find the scope-specific PDF for this transmittal
        let scopePdfPath = frsPdfPath;
        if (templateId && scopePdfs[templateId]) {
          scopePdfPath = getScopePdfPath(scopePdfs[templateId]);
        }
        // Fallback: match by spec_section
        if (!scopePdfPath || scopePdfPath === frsPdfPath) {
          for (const [, entry] of Object.entries(scopePdfs)) {
            if (currentTransmittal && scopeMatchesTransmittal(entry, currentTransmittal)) {
              scopePdfPath = getScopePdfPath(entry);
              break;
            }
          }
        }
        
        // Save the best available file path: PDF first, then Excel
        const resolvedPdfPath = (scopePdfPath && scopePdfPath.toLowerCase().endsWith('.pdf')) ? scopePdfPath 
          : (filePath && filePath.toLowerCase().endsWith('.pdf')) ? filePath 
          : filePath;  // Always save the file path, even if it's Excel
        
        // Save upload status AND file paths to database (scope-filtered)
        const currentTransmittalFiltered = filterParsedDataForTransmittal(currentTransmittal || { specSection: '', spec_section: '' });
        try {
          await axios.put(
            `${API}/projects/${project.id}/project-submittals/${transmittalId}`,
            {
              frs_uploaded: true,
              frs_file_path: resolvedPdfPath,
              parsed_data: currentTransmittalFiltered
            }
          );
        } catch (dbError) {
          console.error('Error saving FRS status to database:', dbError);
        }
        
        // If multi-scope FRS, update ALL matching submittals with their scope-specific PDFs
        if (Object.keys(scopePdfs).length > 1) {
          for (const [scopeTemplateId, scopeEntry] of Object.entries(scopePdfs)) {
            const pdfPath = getScopePdfPath(scopeEntry);
            if (!pdfPath) continue;
            
            // Find matching submittal by template_id OR by this scope entry's spec_section
            const matchingSubmittal = transmittals.find(t => {
              if (t.id === transmittalId) return false;
              const tTemplateId = t.templateId || t.template_id;
              // Direct template ID match
              if (tTemplateId === scopeTemplateId) return true;
              // Spec section match: compare THIS scope entry's spec against the transmittal's spec
              if (scopeMatchesTransmittal(scopeEntry, t)) return true;
              return false;
            });
            if (matchingSubmittal) {
              const matchFiltered = filterParsedDataForTransmittal(matchingSubmittal);
              try {
                await axios.put(
                  `${API}/projects/${project.id}/project-submittals/${matchingSubmittal.id}`,
                  {
                    frs_uploaded: true,
                    frs_file_path: pdfPath,
                    parsed_data: matchFiltered
                  }
                );
              } catch (err) {
                console.error(`Error updating submittal ${matchingSubmittal.name}:`, err);
              }
            }
          }
          
          // Update all transmittals in state with their scope-specific PDFs
          setTransmittals(prev => prev.map(t => {
            const tTemplateId = t.templateId || t.template_id;
            // Match by template ID first, then by spec section of each scope entry
            let tPdfPath = getScopePdfPath(scopePdfs[tTemplateId]);
            if (!tPdfPath) {
              for (const [, entry] of Object.entries(scopePdfs)) {
                if (scopeMatchesTransmittal(entry, t)) {
                  tPdfPath = getScopePdfPath(entry);
                  break;
                }
              }
            }
            const tFiltered = filterParsedDataForTransmittal(t);
            const tLineItems = tFiltered?.line_items || [];
            
            // Always update the transmittal that was uploaded
            if (t.id === transmittalId) {
              return {
                ...t,
                uploading: false,
                uploaded: true,
                frsFilePath: tPdfPath || resolvedPdfPath,
                frsExcelPath: filePath,
                parsedData: tFiltered,
                lineItems: tLineItems,
                createdSubmittals,
                uploadProgress: 100
              };
            }
            
            // Update other transmittals that have matching scope PDFs
            if (tPdfPath) {
              return {
                ...t,
                uploading: false,
                uploaded: true,
                frsFilePath: tPdfPath,
                frsExcelPath: filePath,
                parsedData: tFiltered,
                lineItems: tLineItems,
                uploadProgress: 100
              };
            }
            return t;
          }));
          
          toast.success(`Multi-scope FRS uploaded! Updated ${Object.keys(scopePdfs).length} scopes with individual PDFs`);
        } else {
          // Single scope - just update the current transmittal with filtered data
          const singleFiltered = filterParsedDataForTransmittal(
            transmittals.find(t => t.id === transmittalId) || {}
          );
          setTransmittals(prev => prev.map(t => 
            t.id === transmittalId ? { 
              ...t, 
              uploading: false, 
              uploaded: true, 
              frsFilePath: resolvedPdfPath,
              frsExcelPath: filePath,
              parsedData: singleFiltered,
              lineItems: singleFiltered?.line_items || lineItems,
              createdSubmittals,
              uploadProgress: 100
            } : t
          ));
          
          toast.success(`Final Room Schedule uploaded! Found ${lineItems.length} line items`);
        }
        
        setExpandedTransmittal(transmittalId);
        
        // Auto-build submittal packages from SharePoint
        // For multi-scope FRS, build for ALL updated transmittals (matched by spec_section)
        if (Object.keys(scopePdfs).length > 1) {
          const updatedIds = transmittals
            .filter(t => {
              if (t.id === transmittalId) return true;
              const tTemplateId = t.templateId || t.template_id;
              // Direct template ID match
              if (scopePdfs[tTemplateId]) return true;
              // Spec section match: check if any scope entry matches this transmittal
              for (const [, entry] of Object.entries(scopePdfs)) {
                if (scopeMatchesTransmittal(entry, t)) return true;
              }
              return false;
            })
            .map(t => t.id);
          toast.info(`Building ${updatedIds.length} submittal packages from SharePoint...`, { duration: 15000 });
          let delay = 500;
          for (const id of updatedIds) {
            setTimeout(() => handleBuildPackage(id), delay);
            delay += 1500; // Stagger to avoid overwhelming SharePoint
          }
        } else {
          setTimeout(() => handleBuildPackage(transmittalId), 500);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Upload failed';
      setTransmittals(prev => prev.map(t => 
        t.id === transmittalId ? { ...t, uploading: false, error: errorMessage } : t
      ));
      toast.error(errorMessage);
    }
  };

  // Vendor docs search
  const handleSearchVendorDocs = async (transmittalId, submittalsFromUpload = null) => {
    const transmittal = transmittals.find(t => t.id === transmittalId);
    if (!transmittal?.uploaded && !submittalsFromUpload) {
      toast.error('Please upload Final Room Schedule first');
      return;
    }

    setTransmittals(prev => prev.map(t => 
      t.id === transmittalId ? { ...t, searchingDocs: true } : t
    ));

    try {
      const createdSubmittals = submittalsFromUpload || transmittal.createdSubmittals || [];
      
      if (createdSubmittals.length === 0) {
        toast.error('No submittal created from Final Room Schedule');
        setTransmittals(prev => prev.map(t => 
          t.id === transmittalId ? { ...t, searchingDocs: false } : t
        ));
        return;
      }

      let allVendorDocs = [];
      
      for (const submittal of createdSubmittals) {
        try {
          const formData = new FormData();
          formData.append('submittal_id', submittal.id);
          const response = await axios.post(`${API}/vendors/auto-find-documents`, formData);
          
          if (response.data.success) {
            allVendorDocs = [...allVendorDocs, ...(response.data.documents_found || [])];
          }
        } catch (err) {
          console.warn(`Error searching for submittal ${submittal.id}:`, err);
        }
      }

      setTransmittals(prev => prev.map(t => 
        t.id === transmittalId ? { 
          ...t, 
          searchingDocs: false,
          docsSearched: true,
          vendorDocs: allVendorDocs
        } : t
      ));
      
      if (allVendorDocs.length > 0) {
        toast.success(`Found ${allVendorDocs.length} vendor documents from SharePoint`);
      } else {
        toast.info('No vendor documents found - upload manually below');
      }
    } catch (error) {
      console.error('Vendor docs search error:', error);
      setTransmittals(prev => prev.map(t => 
        t.id === transmittalId ? { ...t, searchingDocs: false, docsSearched: true } : t
      ));
      toast.info('SharePoint not configured - manual document upload available');
    }
  };

  // Manual docs handlers
  const handleManualDocsSelect = (transmittalId, files) => {
    if (!files || files.length === 0) return;
    setTransmittals(prev => prev.map(t => 
      t.id === transmittalId ? { ...t, pendingManualDocs: Array.from(files) } : t
    ));
  };

  const handleUploadManualDocs = async (transmittalId, directFiles = null) => {
    const transmittal = transmittals.find(t => t.id === transmittalId);
    const filesToUpload = directFiles || transmittal?.pendingManualDocs;
    
    if (!filesToUpload?.length) {
      toast.error('Please select files first');
      return;
    }

    setTransmittals(prev => prev.map(t => 
      t.id === transmittalId ? { ...t, uploadingManualDocs: true, pendingManualDocs: filesToUpload } : t
    ));

    try {
      const formData = new FormData();
      filesToUpload.forEach(file => formData.append('files', file));
      formData.append('project_id', project?.id || 'demo-project');

      const response = await axios.post(`${API}/upload/vendor-docs`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadedDocs = response.data.uploaded || response.data.documents || [];
      const newManualDocs = [...(transmittal.manualDocs || []), ...uploadedDocs];
      
      // Save to database
      try {
        await axios.put(
          `${API}/projects/${project.id}/project-submittals/${transmittalId}`,
          { manual_docs: newManualDocs }
        );
      } catch (dbError) {
        console.error('Error saving manual docs to database:', dbError);
      }
      
      setTransmittals(prev => prev.map(t => 
        t.id === transmittalId ? { 
          ...t, 
          uploadingManualDocs: false,
          pendingManualDocs: [],
          manualDocs: newManualDocs
        } : t
      ));
      
      toast.success(`Uploaded ${uploadedDocs.length} product data sheet(s)`);
    } catch (error) {
      console.error('Manual docs upload error:', error);
      setTransmittals(prev => prev.map(t => 
        t.id === transmittalId ? { ...t, uploadingManualDocs: false } : t
      ));
      toast.error('Failed to upload documents');
    }
  };

  const handleRemoveManualDoc = async (transmittalId, docIndex) => {
    const transmittal = transmittals.find(t => t.id === transmittalId);
    const newManualDocs = transmittal.manualDocs?.filter((_, idx) => idx !== docIndex) || [];
    
    // Save to database
    try {
      await axios.put(
        `${API}/projects/${project.id}/project-submittals/${transmittalId}`,
        { manual_docs: newManualDocs }
      );
    } catch (dbError) {
      console.error('Error removing doc from database:', dbError);
    }
    
    setTransmittals(prev => prev.map(t => 
      t.id === transmittalId ? { 
        ...t, 
        manualDocs: newManualDocs
      } : t
    ));
    toast.success('Document removed');
  };

  // Compile submittal PDF
  const handleCompileSubmittal = async (transmittalId) => {
    const transmittal = transmittals.find(t => t.id === transmittalId);
    if (!transmittal) return;

    setCompilingPdf(prev => ({ ...prev, [transmittalId]: true }));

    try {
      const formData = new FormData();
      formData.append('transmittal_id', transmittalId);
      formData.append('transmittal_data', JSON.stringify({
        name: transmittal.name,
        specSection: transmittal.specSection,
        otherTradesItems: transmittal.otherTradesItems || [],
        scopeConcerns: transmittal.scopeConcerns || [],
        fieldMeasurementItems: transmittal.fieldMeasurementItems || [],
        fieldMeasurementRequired: transmittal.fieldMeasurementRequired || '',
        installationItems: transmittal.installationItems || [],
        installationPrepRequired: transmittal.installationPrepRequired || ''
      }));

      // Add product data sheets
      if (transmittal.manualDocs?.length > 0) {
        for (const doc of transmittal.manualDocs) {
          if (doc.path) {
            // Fetch the file from backend and add to form
            const fileResponse = await fetch(`${BACKEND_URL}${doc.path}`);
            const blob = await fileResponse.blob();
            formData.append('product_files', blob, doc.filename || 'product.pdf');
          }
        }
      }

      const response = await axios.post(
        `${API}/projects/${project?.id || 'demo-project'}/compile-submittal`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        setCompiledPdfs(prev => ({
          ...prev,
          [transmittalId]: response.data.compiled_pdf
        }));
        toast.success('Submittal compiled successfully! Click "View/Edit PDF" to review.');
      }
    } catch (error) {
      console.error('Error compiling submittal:', error);
      toast.error('Failed to compile submittal');
    } finally {
      setCompilingPdf(prev => ({ ...prev, [transmittalId]: false }));
    }
  };

  // Open compiled PDF in new tab
  const handleViewCompiledPdf = (transmittalId) => {
    const compiledPdf = compiledPdfs[transmittalId];
    if (compiledPdf?.id) {
      window.open(`${BACKEND_URL}/api/subbuilder/compiled-pdf/${compiledPdf.id}`, '_blank');
    }
  };

  const allUploaded = transmittals.length > 0 && transmittals.every(t => t.uploaded);

  const groupLineItemsByManufacturer = (items) => {
    const grouped = {};
    items.forEach(item => {
      const mfr = item.manufacturer || 'Unknown';
      if (!grouped[mfr]) grouped[mfr] = [];
      grouped[mfr].push(item);
    });
    return grouped;
  };

  // =============================================
  // EDIT VIEW - Full-page template editing
  // =============================================
  if (editingTransmittalId) {
    const editingTransmittal = transmittals.find(t => t.id === editingTransmittalId);
    
    return (
      <div className="space-y-6" data-testid="edit-transmittal-view">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="outline" 
            onClick={() => {
              setEditingTransmittalId(null);
              setEditMode(null);
            }}
            data-testid="back-to-submittals-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Submittals
          </Button>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleSaveSubmittal}
              className="btn-primary"
              disabled={savingSubmittal}
              data-testid="save-submittal-button"
            >
              {savingSubmittal ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" />Save Changes</>
              )}
            </Button>
          </div>
        </div>

        {/* Info Banner - Persistent editing */}
        <div className="force-light-form bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-3">
          <div className="!text-green-600 mt-0.5">
            <CheckCircle className="h-5 w-5" />
          </div>
          <div className="text-sm !text-green-800">
            <p className="font-medium !text-green-800">Persistent Submittal Editing</p>
            <p className="!text-green-600">Changes here are saved permanently to this project's submittal. They will persist even after you leave and return.</p>
          </div>
        </div>

        {/* Submittal Form - always light theme for print-like appearance */}
        <Card className="force-light-form bg-white border-border shadow-sm">
          <CardContent className="p-6">
            {/* Name and Spec Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label className="font-semibold">Scope</Label>
                <Input 
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="mt-1"
                  placeholder="e.g., Wall Protection"
                />
              </div>
              <div>
                <Label className="font-semibold">Spec Section</Label>
                <Input 
                  value={specSection}
                  onChange={(e) => setSpecSection(e.target.value)}
                  className="mt-1"
                  placeholder="e.g., 10 26 00 Wall & Door Protection"
                />
              </div>
            </div>

            {/* Transmittal Info Section - matches PDF layout */}
            <div className="border rounded-lg overflow-hidden mb-6">
              <div className="bg-slate-100 px-4 py-3 border-b">
                <h3 className="font-semibold text-sm text-slate-800 tracking-wide">TRANSMITTAL INFO</h3>
              </div>
              <div className="p-4 bg-white space-y-4">
                {/* Row 1: Submittal No. and Date Submitted */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Submittal No.</Label>
                    <Input 
                      value={submittalNo}
                      onChange={(e) => setSubmittalNo(e.target.value)}
                      className="mt-1"
                      placeholder="e.g., 102800-1.0"
                      data-testid="submittal-no-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Date Submitted</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full mt-1 justify-start text-left font-normal ${!dateSubmitted ? 'text-muted-foreground' : ''}`}
                          data-testid="date-submitted-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dateSubmitted || 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={dateSubmitted ? (() => { try { return parse(dateSubmitted, 'MM/dd/yyyy', new Date()); } catch { return undefined; } })() : undefined}
                          onSelect={(date) => setDateSubmitted(date ? format(date, 'MM/dd/yyyy') : '')}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Row 2: Lead Time, Shipping Time, Install Duration */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Lead Time</Label>
                    {(() => {
                      const LEAD_OPTIONS = ['1-2 Weeks', '2-4 Weeks', '4-6 Weeks', '6-8 Weeks', '8-10 Weeks', '10-12 Weeks', '12-16 Weeks', '16-20 Weeks'];
                      const isPreset = LEAD_OPTIONS.includes(leadTime);
                      const isCustom = leadTime && !isPreset;
                      return (
                        <div className="mt-1">
                          <select
                            value={isPreset ? leadTime : (isCustom ? 'custom' : '')}
                            onChange={(e) => {
                              if (e.target.value === 'custom') setLeadTime('custom');
                              else setLeadTime(e.target.value);
                            }}
                            className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md bg-white text-black focus:outline-none focus:ring-2 focus:ring-primary/50"
                            data-testid="lead-time-select"
                          >
                            <option value="">Select...</option>
                            {LEAD_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            <option value="custom">Custom...</option>
                          </select>
                          {(isCustom || leadTime === 'custom') && (
                            <Input
                              value={leadTime === 'custom' ? '' : leadTime}
                              onChange={(e) => setLeadTime(e.target.value || 'custom')}
                              className="mt-1"
                              placeholder="Enter custom lead time"
                              autoFocus
                              data-testid="lead-time-input"
                            />
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Shipping Time</Label>
                    {(() => {
                      const SHIP_OPTIONS = ['1 Week', '2 Weeks', '3 Weeks', '4 Weeks'];
                      const isPreset = SHIP_OPTIONS.includes(shippingTime);
                      const isCustom = shippingTime && !isPreset;
                      return (
                        <div className="mt-1">
                          <select
                            value={isPreset ? shippingTime : (isCustom ? 'custom' : '')}
                            onChange={(e) => {
                              if (e.target.value === 'custom') setShippingTime('custom');
                              else setShippingTime(e.target.value);
                            }}
                            className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md bg-white text-black focus:outline-none focus:ring-2 focus:ring-primary/50"
                            data-testid="shipping-time-select"
                          >
                            <option value="">Select...</option>
                            {SHIP_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            <option value="custom">Custom...</option>
                          </select>
                          {(isCustom || shippingTime === 'custom') && (
                            <Input
                              value={shippingTime === 'custom' ? '' : shippingTime}
                              onChange={(e) => setShippingTime(e.target.value || 'custom')}
                              className="mt-1"
                              placeholder="Enter custom shipping time"
                              autoFocus
                              data-testid="shipping-time-input"
                            />
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Install Duration</Label>
                    <Input 
                      value={installDuration}
                      onChange={(e) => setInstallDuration(e.target.value)}
                      className="mt-1"
                      placeholder="e.g., 3 days"
                      data-testid="install-duration-input"
                    />
                  </div>
                </div>

                {/* Row 3: Return Submittal Due, Plan Date, Additional Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Return Submittal Due</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full mt-1 justify-start text-left font-normal ${!returnSubmittalDue ? 'text-muted-foreground' : ''}`}
                          data-testid="return-submittal-due-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {returnSubmittalDue || 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={returnSubmittalDue ? (() => { try { return parse(returnSubmittalDue, 'MM/dd/yyyy', new Date()); } catch { return undefined; } })() : undefined}
                          onSelect={(date) => setReturnSubmittalDue(date ? format(date, 'MM/dd/yyyy') : '')}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Plan Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full mt-1 justify-start text-left font-normal ${!planDate ? 'text-muted-foreground' : ''}`}
                          data-testid="plan-date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {planDate || 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={planDate ? (() => { try { return parse(planDate, 'MM/dd/yyyy', new Date()); } catch { return undefined; } })() : undefined}
                          onSelect={(date) => setPlanDate(date ? format(date, 'MM/dd/yyyy') : '')}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Additional Info.</Label>
                    <Input 
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                      className="mt-1"
                      placeholder="Any additional notes"
                      data-testid="additional-info-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Items Attached Section */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-4 py-3 border-b">
                <h3 className="font-semibold text-sm text-gray-800 tracking-wide">ITEMS ATTACHED</h3>
              </div>
              <div className="p-4 bg-white">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                  {[
                    { key: 'product_data', label: 'Product Data' },
                    { key: 'shop_drawings', label: 'Shop Drawings' },
                    { key: 'warranty_samples', label: 'Warranty Samples' },
                    { key: 'leed_docs', label: 'LEED Docs' },
                    { key: 'color_selectors', label: 'Color Selectors' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                      <input
                        type="checkbox"
                        checked={!!itemsAttached[key]}
                        onChange={(e) => setItemsAttached(prev => ({ ...prev, [key]: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                        data-testid={`items-attached-${key}`}
                      />
                      {label}
                    </label>
                  ))}
                  {/* Other 1 */}
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={!!itemsAttached.other1_checked}
                      onChange={(e) => setItemsAttached(prev => ({ ...prev, other1_checked: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                      data-testid="items-attached-other1"
                    />
                    Other:
                    <input
                      type="text"
                      value={itemsAttached.other1_text || ''}
                      onChange={(e) => setItemsAttached(prev => ({ ...prev, other1_text: e.target.value }))}
                      className="border-b border-gray-300 bg-transparent text-sm text-gray-900 focus:outline-none focus:border-blue-500 w-24 px-1"
                      placeholder="..."
                      data-testid="items-attached-other1-text"
                    />
                  </label>
                  {[
                    { key: 'quantity_takeoff', label: 'Quantity Takeoff' },
                    { key: 'msds_sheets', label: 'MSDS Sheets' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                      <input
                        type="checkbox"
                        checked={!!itemsAttached[key]}
                        onChange={(e) => setItemsAttached(prev => ({ ...prev, [key]: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                        data-testid={`items-attached-${key}`}
                      />
                      {label}
                    </label>
                  ))}
                  {/* Other 2 */}
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={!!itemsAttached.other2_checked}
                      onChange={(e) => setItemsAttached(prev => ({ ...prev, other2_checked: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                      data-testid="items-attached-other2"
                    />
                    Other:
                    <input
                      type="text"
                      value={itemsAttached.other2_text || ''}
                      onChange={(e) => setItemsAttached(prev => ({ ...prev, other2_text: e.target.value }))}
                      className="border-b border-gray-300 bg-transparent text-sm text-gray-900 focus:outline-none focus:border-blue-500 w-24 px-1"
                      placeholder="..."
                      data-testid="items-attached-other2-text"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Submittal Sections */}
            <div className="border rounded-lg overflow-hidden mt-4">
              <EditableListSection
                title="THE FOLLOWING ARE INSTALLED BY OTHERS"
                items={otherTradesItems}
                setItems={setOtherTradesItems}
                bgColor="bg-amber-50"
              />
              <EditableListSection
                title="SCOPE SPECIFIC / PROJECT SPECIFIC CONCERNS"
                items={scopeConcerns}
                setItems={setScopeConcerns}
              />
              <EditableListSection
                title="FIELD MEASUREMENT PREP (Y/N)"
                items={fieldMeasurementItems}
                setItems={setFieldMeasurementItems}
                bgColor="bg-blue-50"
                showYesNoSelector={true}
                yesNoValue={fieldMeasurementRequired}
                onYesNoChange={setFieldMeasurementRequired}
              />
              <EditableListSection
                title="INSTALLATION PREP (Y/N)"
                items={installationItems}
                setItems={setInstallationItems}
                bgColor="bg-green-50"
                showYesNoSelector={true}
                yesNoValue={installationPrepRequired}
                onYesNoChange={setInstallationPrepRequired}
              />
              
              {/* Attic Stock Required Section - Now with editable items */}
              <EditableListSection
                title="ATTIC STOCK REQUIRED (Y/N)"
                items={atticStockItems}
                setItems={setAtticStockItems}
                bgColor="bg-purple-50"
                showYesNoSelector={true}
                yesNoValue={atticStockRequired}
                onYesNoChange={setAtticStockRequired}
              />
              
              {/* Proposal Exclusions Section */}
              <EditableListSection
                title="PROPOSAL EXCLUSIONS"
                items={proposalExclusions}
                setItems={setProposalExclusions}
                bgColor="bg-rose-50"
                allowPaste={true}
              />
            </div>

            {/* Summary */}
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium text-foreground mb-2">Submittal Summary:</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 text-sm text-muted-foreground">
                <div>{otherTradesItems.filter(i => i.trim()).length} Installed By Others items</div>
                <div>{scopeConcerns.filter(i => i.trim()).length} Scope Concerns</div>
                <div>{fieldMeasurementItems.filter(i => i.trim()).length} Field Measurement items</div>
                <div>{installationItems.filter(i => i.trim()).length} Installation Prep items</div>
                <div>{proposalExclusions.filter(i => i.trim()).length} Proposal Exclusions</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // =============================================
  // MAIN VIEW - Submittals list
  // =============================================
  return (
    <div className="space-y-6" data-testid="upload-step">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileUp className="h-5 w-5 text-primary" />
            Product Data & Color/Finish Options
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Transmittals List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Loader2 className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
              <p className="text-muted-foreground">Loading submittals...</p>
            </div>
          </CardContent>
        </Card>
      ) : transmittals.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No transmittals selected. Please go back to Transmittals step.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {transmittals.map((transmittal, index) => (
            <Card 
              key={transmittal.id} 
              className={`transition-all ${transmittal.uploaded ? 'border-green-500/50 bg-green-500/5' : ''}`}
              data-testid={`transmittal-card-${index}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center h-10 w-10 rounded-xl ${transmittal.uploaded ? 'bg-green-500/20' : 'bg-primary/10'}`}>
                      <span className={`text-lg font-bold ${transmittal.uploaded ? 'text-green-500' : 'text-primary'}`}>{index + 1}</span>
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {transmittal.name}
                        {transmittal.uploaded && (
                          <Badge variant="success" className="gap-1 bg-green-500/20 text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            Uploaded
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {transmittal.specSection}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDuplicateSubmittal(transmittal.id)}
                      disabled={duplicatingId === transmittal.id}
                      title="Add another transmittal of this scope"
                      data-testid={`duplicate-transmittal-${index}`}
                    >
                      {duplicatingId === transmittal.id ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Copy className="h-4 w-4 mr-1" />
                      )}
                      Duplicate
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteSubmittal(transmittal.id)}
                      title="Remove submittal from project"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200"
                      data-testid={`remove-transmittal-${index}`}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* File Upload Section */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Final Room Schedule (FRS)
                  </Label>
                  
                  {/* Show upload button or uploaded file info */}
                  {!transmittal.uploaded ? (
                    <>
                    <div className="flex items-center gap-3">
                      <Button variant="mode"
                        size="sm"
                        onClick={() => handleOpenEdit(transmittal.id)}
                        className="gap-1.5 text-xs"
                        data-testid={`edit-transmittal-${index}`}
                      >
                        <Edit className="h-3.5 w-3.5" />
                        Edit
                      </Button>
                      <DropButton
                        onFilesDropped={(file) => {
                          handleFileSelect(transmittal.id, file);
                          handleUploadFRS(transmittal.id, file);
                        }}
                        accept={{
                          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                          'application/vnd.ms-excel': ['.xls'],
                          'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                          'application/msword': ['.doc'],
                          'application/pdf': ['.pdf']
                        }}
                        multiple={false}
                        disabled={transmittal.uploading}
                        isUploading={transmittal.uploading}
                        testId={`upload-frs-btn-${index}`}
                        size="sm"
                        className="text-xs"
                      >
                        <><Upload className="h-3.5 w-3.5" />FRS</>
                      </DropButton>
                      <DropButton
                        onFilesDropped={(files) => {
                          const filesArray = Array.isArray(files) ? files : [files];
                          handleAppendToPackage(transmittal.id, filesArray, 'product_data');
                        }}
                        accept={{ 'application/pdf': ['.pdf'] }}
                        multiple={true}
                        disabled={transmittal.uploadingManualDocs}
                        isUploading={transmittal.uploadingManualDocs}
                        testId={`add-product-sheets-btn-${index}`}
                        size="sm"
                        className="text-xs"
                      >
                        {transmittal.uploadingManualDocs ? (
                          <><Loader2 className="h-3.5 w-3.5 animate-spin" />Uploading</>
                        ) : (
                          <><Upload className="h-3.5 w-3.5" />Product Data</>
                        )}
                      </DropButton>
                      <DropButton
                        onFilesDropped={(files) => {
                          const filesArray = Array.isArray(files) ? files : [files];
                          handleAppendToPackage(transmittal.id, filesArray, 'color_card');
                        }}
                        accept={{ 'application/pdf': ['.pdf'], 'image/*': ['.png', '.jpg', '.jpeg'] }}
                        multiple={true}
                        testId={`color-options-btn-${index}`}
                        size="sm"
                        className="text-xs"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        Colors
                      </DropButton>
                      <Button variant="nav"
                        size="sm"
                        onClick={() => setViewingSubmittal(transmittal)}
                        className="gap-1.5 text-xs"
                        data-testid={`view-submittal-btn-${index}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </Button>
                    </div>
                    </>
                  ) : (
                    /* Show uploaded file info with delete option */
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                          <div>
                            <p className="text-sm font-medium text-green-800 dark:text-green-200">
                              FRS Uploaded Successfully
                            </p>
                            <p className="text-xs text-green-600 dark:text-green-400">
                              {transmittal.frsFile?.name || 'File uploaded'} • {transmittal.lineItems?.length || 0} line items parsed
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleClearFRS(transmittal.id)}
                          className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                          data-testid={`delete-frs-btn-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete & Re-upload
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="mode"
                          size="sm"
                          onClick={() => handleOpenEdit(transmittal.id)}
                          className="gap-1.5 text-xs"
                          data-testid={`edit-transmittal-uploaded-${index}`}
                        >
                          <Edit className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                        <DropButton
                          onFilesDropped={(files) => {
                            const filesArray = Array.isArray(files) ? files : [files];
                            handleAppendToPackage(transmittal.id, filesArray, 'product_data');
                          }}
                          accept={{ 'application/pdf': ['.pdf'] }}
                          multiple={true}
                          disabled={transmittal.uploadingManualDocs}
                          isUploading={transmittal.uploadingManualDocs}
                          testId={`add-product-sheets-uploaded-btn-${index}`}
                          size="sm"
                          className="text-xs"
                        >
                          {transmittal.uploadingManualDocs ? (
                            <><Loader2 className="h-3.5 w-3.5 animate-spin" />Uploading</>
                          ) : (
                            <><Upload className="h-3.5 w-3.5" />Product Data</>
                          )}
                        </DropButton>
                        <DropButton
                          onFilesDropped={(files) => {
                            const filesArray = Array.isArray(files) ? files : [files];
                            handleAppendToPackage(transmittal.id, filesArray, 'color_card');
                          }}
                          accept={{ 'application/pdf': ['.pdf'], 'image/*': ['.png', '.jpg', '.jpeg'] }}
                          multiple={true}
                          testId={`color-options-uploaded-btn-${index}`}
                          size="sm"
                          className="text-xs"
                        >
                          <Upload className="h-3.5 w-3.5" />
                          Colors
                        </DropButton>
                        <Button variant="nav"
                          size="sm"
                          onClick={() => setViewingSubmittal(transmittal)}
                          className="gap-1.5 text-xs"
                          data-testid={`view-submittal-uploaded-btn-${index}`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          View
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Build Status Line */}
                  {buildingPackage === transmittal.id && (
                    <div className="flex items-center gap-2 p-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md animate-pulse" data-testid={`build-status-${index}`}>
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                      <p className="text-sm text-blue-700 dark:text-blue-300">Building submittal package from SharePoint...</p>
                    </div>
                  )}
                  {buildComplete === transmittal.id && (
                    <div className="flex items-center gap-2 p-2.5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-md transition-opacity duration-500" data-testid={`build-complete-${index}`}>
                      <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      <p className="text-sm text-emerald-700 dark:text-emerald-300">Submittal package built successfully</p>
                    </div>
                  )}

                  {transmittal.uploading && (
                    <div className="space-y-2">
                      <Progress value={transmittal.uploadProgress} />
                      <p className="text-sm text-muted-foreground text-center">{transmittal.uploadProgress}%</p>
                    </div>
                  )}

                  {/* Manual Upload Progress (Product Data / Color Card) */}
                  {manualUploadProgress[transmittal.id] && (
                    <div className="space-y-2 p-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md" data-testid={`manual-upload-progress-${index}`}>
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Adding {manualUploadProgress[transmittal.id].type === 'color_card' ? 'color card' : 'product data'} to end of package...
                        </p>
                      </div>
                      <Progress value={manualUploadProgress[transmittal.id].progress} />
                      <p className="text-xs text-muted-foreground text-center">
                        {manualUploadProgress[transmittal.id].progress}%
                      </p>
                    </div>
                  )}

                  {transmittal.error && (
                    <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                      <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
                      <p className="text-sm text-destructive">{transmittal.error}</p>
                    </div>
                  )}

                  {/* Missing Items Report - only show when FRS is uploaded */}
                  {transmittal.uploaded && transmittal.missingItems && transmittal.missingItems.length > 0 && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md" data-testid={`missing-report-${index}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                          Missing Documents Report ({transmittal.missingItems.length} item{transmittal.missingItems.length !== 1 ? 's' : ''})
                        </p>
                      </div>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mb-2">
                        Click the upload icon to manually provide a product data sheet for each missing item.
                      </p>
                      {/* Apply manufacturer to all missing items */}
                      {transmittal.missingItems.some(item => !item.manufacturer || (item.reason || '').toLowerCase().includes('manufacturer not found')) && (
                        <div className="flex items-center gap-2 mb-2 p-2 bg-amber-100/60 rounded border border-amber-200" data-testid={`apply-mfg-all-${index}`}>
                          <span className="text-xs font-medium text-amber-800 whitespace-nowrap">Set manufacturer for all:</span>
                          <Input
                            type="text"
                            placeholder="Manufacturer name..."
                            value={mfgSearchInput[`${transmittal.id}|_all`] || ''}
                            onChange={(e) => setMfgSearchInput(prev => ({ ...prev, [`${transmittal.id}|_all`]: e.target.value }))}
                            className="h-7 text-xs flex-1 max-w-[200px] bg-white"
                            data-testid={`mfg-all-input-${index}`}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              const mfgName = (mfgSearchInput[`${transmittal.id}|_all`] || '').trim();
                              if (!mfgName) { toast.error('Please enter a manufacturer name'); return; }
                              // Apply manufacturer name to all missing items in UI
                              const updates = {};
                              transmittal.missingItems.forEach((item, mi) => {
                                const isMissing = !item.manufacturer || (item.reason || '').toLowerCase().includes('manufacturer not found');
                                if (isMissing) {
                                  const key = `${transmittal.id}|${item.product_index ?? item.model}`;
                                  updates[key] = mfgName;
                                }
                              });
                              setMfgSearchInput(prev => ({ ...prev, ...updates }));
                              toast.success(`Applied "${mfgName}" to ${Object.keys(updates).length} items. Searching SharePoint...`);
                              // Trigger rebuild with the new manufacturer
                              try {
                                setBuildingPackage(transmittal.id);
                                const authToken = localStorage.getItem('auth_token');
                                const rebuildResp = await axios.post(
                                  `${API}/projects/${project.id}/project-submittals/${transmittal.id}/build-package?override_manufacturer=${encodeURIComponent(mfgName)}`,
                                  {},
                                  { headers: { Authorization: `Bearer ${authToken}` } }
                                );
                                if (rebuildResp.data?.success) {
                                  toast.success('SharePoint search complete — missing docs updated');
                                  // Refresh transmittal data
                                  await loadOrCreateSubmittals();
                                }
                              } catch (err) {
                                console.error('Rebuild after apply-all failed:', err);
                                toast.error(err.response?.data?.detail || 'Search failed — check manufacturer name');
                              } finally {
                                setBuildingPackage(null);
                              }
                            }}
                            disabled={!(mfgSearchInput[`${transmittal.id}|_all`] || '').trim() || buildingPackage === transmittal.id}
                            className="h-7 text-xs px-2 border-amber-400 text-amber-800 hover:bg-amber-100"
                            data-testid={`mfg-apply-all-btn-${index}`}
                          >
                            {buildingPackage === transmittal.id ? (
                              <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Searching...</>
                            ) : (
                              <><Search className="h-3 w-3 mr-1" /> Apply & Search</>
                            )}
                          </Button>
                        </div>
                      )}
                      <div className="space-y-2">
                        {transmittal.missingItems.map((item, mi) => {
                          const isUploading = uploadingMissingItem === `${transmittal.id}|${item.model}`;
                          const searchKey = `${transmittal.id}|${item.product_index ?? item.model}`;
                          const isSearching = mfgSearching === searchKey;
                          const searchResult = mfgSearchResults[searchKey];
                          const isMfgMissing = !item.manufacturer || (item.reason || '').toLowerCase().includes('manufacturer not found');
                          return (
                            <div key={mi} className="text-xs text-amber-900 dark:text-amber-100 py-1.5 px-2 rounded hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors" data-testid={`missing-item-${index}-${mi}`}>
                              <div className="flex items-center gap-2">
                                <span className="font-medium min-w-[100px]">{item.manufacturer || '—'}</span>
                                <span className="text-amber-700 dark:text-amber-300 min-w-[100px]">{item.model}</span>
                                {item.description && <span className="text-amber-600 dark:text-amber-400 truncate flex-1">- {item.description}</span>}
                                <label
                                  className={`ml-auto flex items-center gap-1 cursor-pointer text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 transition-colors ${isUploading ? 'pointer-events-none opacity-60' : ''}`}
                                  data-testid={`upload-missing-btn-${index}-${mi}`}
                                >
                                  {isUploading ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Upload className="h-3.5 w-3.5" />
                                  )}
                                  <input
                                    type="file"
                                    accept=".pdf"
                                    className="hidden"
                                    disabled={isUploading}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        handleUploadMissingDoc(transmittal.id, item, file);
                                        e.target.value = '';
                                      }
                                    }}
                                  />
                                </label>
                              </div>
                              {/* Manual manufacturer search — shown when manufacturer is missing/not found */}
                              {isMfgMissing && (
                                <div className="mt-1.5 flex items-center gap-1.5 pl-1">
                                  <Input
                                    type="text"
                                    placeholder="Type manufacturer name..."
                                    value={mfgSearchInput[searchKey] || ''}
                                    onChange={(e) => setMfgSearchInput(prev => ({ ...prev, [searchKey]: e.target.value }))}
                                    className="h-7 text-xs flex-1 max-w-[200px] bg-white"
                                    data-testid={`mfg-search-input-${index}-${mi}`}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearchManufacturer(transmittal.id, item, searchKey); }}
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleSearchManufacturer(transmittal.id, item, searchKey)}
                                    disabled={isSearching || !(mfgSearchInput[searchKey] || '').trim()}
                                    className="h-7 text-xs px-2"
                                    data-testid={`mfg-search-btn-${index}-${mi}`}
                                  >
                                    {isSearching ? <Loader2 className="h-3 w-3 animate-spin" /> : <FolderSearch className="h-3 w-3 mr-1" />}
                                    Search
                                  </Button>
                                </div>
                              )}
                              {/* Search results */}
                              {searchResult?.documents?.length > 0 && (
                                <div className="mt-1.5 ml-1 space-y-1 bg-white/60 rounded p-1.5 border border-amber-200">
                                  <p className="text-[10px] font-medium text-amber-800">Found in {searchResult.manufacturer_folder}:</p>
                                  {searchResult.documents.slice(0, 5).map((doc, di) => (
                                    <div key={di} className="flex items-center gap-1.5 text-[10px]">
                                      <FileText className="h-3 w-3 text-amber-600 shrink-0" />
                                      <span className="truncate flex-1">{doc.name}</span>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleAttachSearchResult(transmittal.id, item, doc)}
                                        className="h-5 text-[10px] px-1.5 border-green-300 text-green-700 hover:bg-green-50"
                                        data-testid={`attach-doc-${index}-${mi}-${di}`}
                                      >
                                        <Plus className="h-2.5 w-2.5 mr-0.5" /> Attach
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {searchResult && !searchResult.success && (
                                <p className="mt-1 ml-1 text-[10px] text-amber-600">{searchResult.message}</p>
                              )}
                              {searchResult?.available_manufacturers?.length > 0 && (
                                <p className="mt-0.5 ml-1 text-[10px] text-amber-500">
                                  Available: {searchResult.available_manufacturers.slice(0, 5).join(', ')}{searchResult.available_manufacturers.length > 5 ? '...' : ''}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Continue Buttons */}
      {transmittals.length > 0 && (
        <div className="flex justify-end gap-2">
          <Button
            onClick={() => onComplete({ transmittals, target: 'drawings' })}
            disabled={!allUploaded}
            variant="outline"
            size="lg"
            className="gap-2"
            data-testid="continue-to-drawings-btn"
          >
            Continue to Drawings
          </Button>
          <Button variant="nav"
            onClick={() => onComplete({ transmittals, target: 'annotate' })}
            disabled={!allUploaded}
            size="lg"
            className="gap-2"
            data-testid="continue-to-annotate-btn"
          >
            Continue to Annotate
            {allUploaded && <CheckCircle className="h-4 w-4" />}
          </Button>
        </div>
      )}

      {/* View Submittal Dialog - Full Document Preview */}
      {viewingSubmittal && (() => {
        // Always use the latest transmittal data from state
        const currentData = transmittals.find(t => t.id === viewingSubmittal.id) || viewingSubmittal;
        return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="force-light-form bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Eye className="h-5 w-5" />
                {currentData.name} - Document Preview
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewingSubmittal(null)}
                data-testid="close-view-submittal"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {/* Document Order: Transmittal > FRS > Product Data > Color Cards > LEED > Warranty > MSDS */}
              {(() => {
                // Parse vendor_docs into groups by type
                const docs = Array.isArray(currentData.vendorDocs) ? currentData.vendorDocs : [];
                const productData = docs.filter(d => d.type === 'product_data');
                const colorCards = docs.filter(d => d.type === 'color_card');
                const leedDocs = docs.filter(d => d.type === 'leed');
                const warrantyDocs = docs.filter(d => d.type === 'warranty');
                const msdsDocs = docs.filter(d => d.type === 'msds');
                const manualDocs = Array.isArray(currentData.manualDocs) ? currentData.manualDocs : [];
                let sectionNum = 0;

                return (
                  <>
                    {/* 1. Transmittal */}
                    <div className="border-b">
                      <div className="bg-primary/10 px-4 py-2 flex items-center gap-2">
                        <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">{++sectionNum}</span>
                        <span className="font-semibold">Transmittal</span>
                        <button onClick={async () => {
                          try {
                            const token = localStorage.getItem('auth_token');
                            const res = await fetch(`${API}/projects/${project?.id}/project-submittals/${currentData.id}/transmittal-pdf`, {
                              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                            });
                            if (!res.ok) throw new Error(`HTTP ${res.status}`);
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url; a.download = `transmittal_${currentData.id}.pdf`;
                            a.click(); URL.revokeObjectURL(url);
                          } catch (e) { console.error('Download failed:', e); }
                        }} className="ml-auto text-xs text-primary hover:underline flex items-center gap-1">
                          <Download className="h-3 w-3" /> Download PDF
                        </button>
                      </div>
                      <div className="bg-gray-100 p-4">
                        <BlobPDFViewer
                          file={`${API}/projects/${project?.id}/project-submittals/${currentData.id}/transmittal-pdf`}
                          title="Transmittal"
                          maxHeight="500px"
                          showControls={true}
                        />
                      </div>
                    </div>

                    {/* 2. FRS */}
                    <div className="border-b">
                      <div className="bg-primary/10 px-4 py-2 flex items-center gap-2">
                        <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">{++sectionNum}</span>
                        <span className="font-semibold">Final Room Schedule (FRS)</span>
                      </div>
                      <div className="bg-gray-100 p-4">
                        {(currentData.uploaded || currentData.frsUploaded) && currentData.frsFilePath ? (
                          (() => {
                            const filename = currentData.frsFilePath.split('/').pop();
                            const isPdf = filename.toLowerCase().endsWith('.pdf');
                            const fileUrl = isPdf ? `${API}/files/${filename}` : `${API}/projects/${project?.id}/project-submittals/${currentData.id}/frs-pdf`;
                            return (
                              <>
                                <BlobPDFViewer file={fileUrl} title="FRS Document" maxHeight="500px" showControls={true} />
                                <button onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('auth_token');
                                    const res = await fetch(fileUrl, {
                                      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                                    });
                                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url; a.download = filename || 'frs.pdf';
                                    a.click(); URL.revokeObjectURL(url);
                                  } catch (e) { console.error('Download failed:', e); }
                                }} className="inline-flex items-center gap-1 mt-2 text-xs text-primary hover:underline">
                                  <Download className="h-3 w-3" /> Download FRS PDF
                                </button>
                              </>
                            );
                          })()
                        ) : (
                          <div className="bg-white border shadow-sm p-8 text-center">
                            <p className="text-gray-400 italic">No FRS uploaded yet</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 3. Product Data Sheets */}
                    {(productData.length > 0 || manualDocs.length > 0) && (
                      <div className="border-b">
                        <div className="bg-primary/10 px-4 py-2 flex items-center gap-2">
                          <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">{++sectionNum}</span>
                          <span className="font-semibold">Product Data Sheets</span>
                          <Badge variant="secondary" className="ml-2">{productData.length + manualDocs.length} document(s)</Badge>
                        </div>
                        <div className="bg-gray-100 p-4 space-y-4">
                          {productData.map((doc, i) => {
                            const fileUrl = doc.file_url ? `${API}${doc.file_url}` : null;
                            return (
                              <div key={`pd-${i}`} className="bg-white border shadow-sm rounded-lg overflow-hidden">
                                <div className="px-4 py-2 bg-blue-50 border-b flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-blue-600" />
                                    <span className="text-sm font-medium">{doc.name}</span>
                                    <span className="text-xs text-gray-500">({doc.source})</span>
                                  </div>
                                  {fileUrl && (
                                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                                      <Download className="h-3 w-3" /> Download
                                    </a>
                                  )}
                                </div>
                                {fileUrl ? (
                                  <BlobPDFViewer file={fileUrl} title={doc.name} maxHeight="400px" showControls={true} />
                                ) : (
                                  <div className="p-6 text-center text-gray-400">
                                    <p>File: {doc.source || doc.name}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {manualDocs.map((doc, i) => {
                            const filename = doc.path?.split('/').pop() || doc.filename || `Manual Doc ${i + 1}`;
                            const fileUrl = doc.url || (doc.path ? `${API}/files/${doc.path.split('/').pop()}` : null);
                            return (
                              <div key={`manual-${i}`} className="bg-white border shadow-sm rounded-lg overflow-hidden">
                                <div className="px-4 py-2 bg-gray-50 border-b flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4" />
                                    <span className="text-sm font-medium">{doc.filename || filename}</span>
                                  </div>
                                  {fileUrl && (
                                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                                      <Download className="h-3 w-3" /> Download
                                    </a>
                                  )}
                                </div>
                                {fileUrl && filename.toLowerCase().endsWith('.pdf') ? (
                                  <BlobPDFViewer file={fileUrl} title={doc.filename || filename} maxHeight="400px" showControls={true} />
                                ) : (
                                  <div className="p-6 text-center text-gray-400"><p>Preview not available</p></div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* 4. Color Cards */}
                    {colorCards.length > 0 && (
                      <div className="border-b">
                        <div className="bg-primary/10 px-4 py-2 flex items-center gap-2">
                          <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">{++sectionNum}</span>
                          <span className="font-semibold">Color Cards</span>
                          <Badge variant="secondary" className="ml-2">{colorCards.length} card(s)</Badge>
                        </div>
                        <div className="bg-gray-100 p-4 space-y-4">
                          {colorCards.map((doc, i) => {
                            const fileUrl = doc.file_url ? `${API}${doc.file_url}` : null;
                            return (
                              <div key={`color-${i}`} className="bg-white border shadow-sm rounded-lg overflow-hidden">
                                <div className="px-4 py-2 bg-purple-50 border-b flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Palette className="h-4 w-4 text-purple-600" />
                                    <span className="text-sm font-medium">{doc.name}</span>
                                  </div>
                                  {fileUrl && (
                                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                                      <Download className="h-3 w-3" /> Download
                                    </a>
                                  )}
                                </div>
                                {fileUrl ? (
                                  <BlobPDFViewer file={fileUrl} title={doc.name} maxHeight="400px" showControls={true} />
                                ) : (
                                  <div className="p-6 text-center text-gray-400"><p>File: {doc.source || doc.name}</p></div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* 5. LEED Docs */}
                    {leedDocs.length > 0 && (
                      <div className="border-b">
                        <div className="bg-primary/10 px-4 py-2 flex items-center gap-2">
                          <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">{++sectionNum}</span>
                          <span className="font-semibold">LEED Documents</span>
                          <Badge variant="secondary" className="ml-2">{leedDocs.length}</Badge>
                        </div>
                        <div className="bg-gray-100 p-4 space-y-4">
                          {leedDocs.map((doc, i) => {
                            const fileUrl = doc.file_url ? `${API}${doc.file_url}` : null;
                            return (
                              <div key={`leed-${i}`} className="bg-white border shadow-sm rounded-lg overflow-hidden">
                                <div className="px-4 py-2 bg-green-50 border-b flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-green-600" />
                                  <span className="text-sm font-medium">{doc.name}</span>
                                </div>
                                {fileUrl ? <BlobPDFViewer file={fileUrl} title={doc.name} maxHeight="400px" showControls={true} /> : <div className="p-6 text-center text-gray-400"><p>File: {doc.source}</p></div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* 6. Warranty */}
                    {warrantyDocs.length > 0 && (
                      <div className="border-b">
                        <div className="bg-primary/10 px-4 py-2 flex items-center gap-2">
                          <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">{++sectionNum}</span>
                          <span className="font-semibold">Warranty Documents</span>
                          <Badge variant="secondary" className="ml-2">{warrantyDocs.length}</Badge>
                        </div>
                        <div className="bg-gray-100 p-4 space-y-4">
                          {warrantyDocs.map((doc, i) => {
                            const fileUrl = doc.file_url ? `${API}${doc.file_url}` : null;
                            return (
                              <div key={`warranty-${i}`} className="bg-white border shadow-sm rounded-lg overflow-hidden">
                                <div className="px-4 py-2 bg-amber-50 border-b flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-amber-600" />
                                  <span className="text-sm font-medium">{doc.name}</span>
                                </div>
                                {fileUrl ? <BlobPDFViewer file={fileUrl} title={doc.name} maxHeight="400px" showControls={true} /> : <div className="p-6 text-center text-gray-400"><p>File: {doc.source}</p></div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* 7. MSDS */}
                    {msdsDocs.length > 0 && (
                      <div className="border-b">
                        <div className="bg-primary/10 px-4 py-2 flex items-center gap-2">
                          <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">{++sectionNum}</span>
                          <span className="font-semibold">MSDS Sheets</span>
                          <Badge variant="secondary" className="ml-2">{msdsDocs.length}</Badge>
                        </div>
                        <div className="bg-gray-100 p-4 space-y-4">
                          {msdsDocs.map((doc, i) => {
                            const fileUrl = doc.file_url ? `${API}${doc.file_url}` : null;
                            return (
                              <div key={`msds-${i}`} className="bg-white border shadow-sm rounded-lg overflow-hidden">
                                <div className="px-4 py-2 bg-rose-50 border-b flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-rose-600" />
                                  <span className="text-sm font-medium">{doc.name}</span>
                                </div>
                                {fileUrl ? <BlobPDFViewer file={fileUrl} title={doc.name} maxHeight="400px" showControls={true} /> : <div className="p-6 text-center text-gray-400"><p>File: {doc.source}</p></div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <Button variant="nav" onClick={() => setViewingSubmittal(null)} data-testid="close-view-submittal-btn">
                Close
              </Button>
            </div>
          </div>
        </div>
      );
      })()}

      {/* Color Options Dialog (placeholder) */}
      {Object.entries(colorOptionsOpen).map(([id, isOpen]) => isOpen && (
        <div key={id} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Color Options
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setColorOptionsOpen(prev => ({ ...prev, [id]: false }))}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4">
              <p className="text-muted-foreground text-sm mb-4">
                Upload color cards or select color options for this submittal.
              </p>
              <div className="space-y-3">
                <Button variant="outline" className="w-full gap-2">
                  <Upload className="h-4 w-4" />
                  Upload Color Card
                </Button>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end">
              <Button onClick={() => setColorOptionsOpen(prev => ({ ...prev, [id]: false }))}>
                Close
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
